import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { flushSync } from 'react-dom';
import JSZip from 'jszip';
import { useParams } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useApi } from '../../../lib/api';
import { useWorkspace } from '../../../lib/workspace';
import { getSocket } from '../../../lib/socket';
import { notifyChatUnreadChanged } from '../../../lib/chatUnreadBus';
import { loadOutbox, addToOutbox, removeFromOutbox, migrateLegacyLocalStorageOutbox, isOutboxDurable } from '../../../lib/chatOutbox';
import { useSpacesUpload, validateFiles, ACCEPT_STRING } from '../../../lib/useSpacesUpload';
import { AttachmentDisplay, PendingFilePreview, renderMixedContent } from '../../../components/shared/AttachmentComponents';
import { useMentionInContentEditable, extractTextWithMentions } from '../../../hooks/useMentionInContentEditable';
import { MessageSquare, Plus, Search, Send, X, Users, User, Check, Pin, Reply, Trash2, Edit2, ChevronDown, Smile, Shield, Bell, BellOff, VolumeX, Paperclip, Forward, Download, Copy, FileText } from 'lucide-react';
import './Chat.css';

// ─── Emoji constants ────────────────────────────────────────────────────────
const QUICK_EMOJIS = ['👍', '🔥', '😂', '❤️', '😮', '😢'];
const ALL_EMOJIS = [
  '👍','👎','❤️','🔥','😂','😮','😢','😡','🎉','🙏',
  '👏','💯','🤔','😍','🥰','😎','🤣','😅','😊','😇',
  '🤯','🥳','😴','🤗','😏','🙄','😬','🤐','😤','😔',
  '👀','💪','🤝','✅','⭐','🚀','💡','📌','🔔','💬',
];

// ─── Nudge sound (Web Audio API — no file needed) ───────────────────────────
function playNudgeSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const times = [0, 0.18, 0.36];
    times.forEach(offset => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime + offset);
      osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + offset + 0.12);
      gain.gain.setValueAtTime(0.4, ctx.currentTime + offset);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + offset + 0.14);
      osc.start(ctx.currentTime + offset);
      osc.stop(ctx.currentTime + offset + 0.15);
    });
    setTimeout(() => ctx.close(), 1000);
  } catch (_) {}
}

// ─── Helpers ────────────────────────────────────────────────────────────────
const URL_RE = /https?:\/\/[^\s<>"']+/g; // kept for legacy; COMBINED_RE used in renderWithLinks

function formatDateLabel(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const msgDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diff = today - msgDay;
  if (diff === 0) return 'Today';
  if (diff === 86400000) return 'Yesterday';
  return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
}

const MENTION_RE = /@\[(\d+):([^\]]+)\]/g;
const COMBINED_RE = new RegExp(`${MENTION_RE.source}|${/https?:\/\/[^\s<>"]+|www\.[^\s<>"]+/.source}`, 'g');

function renderWithLinks(text) {
  const parts = [];
  let last = 0;
  let match;
  COMBINED_RE.lastIndex = 0;
  while ((match = COMBINED_RE.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    if (match[1] !== undefined) {
      // mention token @[userId:Name]
      parts.push(
        <span key={match.index} className="mention-chip">@{match[2]}</span>
      );
    } else {
      parts.push(
        <a key={match.index} href={match[0]} target="_blank" rel="noopener noreferrer" className="chat-link">
          {match[0]}
        </a>
      );
    }
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

// ─── ReactionPill — smart tooltip that flips above/below and stays on hover ──
function ReactionPill({ r, iReacted, names, onReact }) {
  const [visible, setVisible] = useState(false);
  const [above, setAbove] = useState(true);
  const wrapRef = useRef(null);
  const hideTimer = useRef(null);

  const show = () => {
    clearTimeout(hideTimer.current);
    if (wrapRef.current) {
      const rect = wrapRef.current.getBoundingClientRect();
      const spaceAbove = rect.top;
      const spaceBelow = window.innerHeight - rect.bottom;
      // show above only if there's at least 20px above AND more space above than below
      setAbove(spaceAbove >= 20 && spaceAbove >= spaceBelow);
    }
    setVisible(true);
  };
  const hide = () => { hideTimer.current = setTimeout(() => setVisible(false), 150); };

  return (
    <div
      ref={wrapRef}
      className="chat-reaction-pill-wrap"
      onMouseEnter={show}
      onMouseLeave={hide}
    >
      <button
        className={`chat-reaction-pill${iReacted ? ' mine' : ''}`}
        onClick={onReact}
      >
        {r.emoji} <span>{r.count}</span>
      </button>
      {names.length > 0 && visible && (
        <div
          className={`chat-reaction-tooltip visible${above ? ' above' : ' below'}`}
          onMouseEnter={show}
          onMouseLeave={hide}
        >
          <div className="chat-reaction-tooltip-header">
            <span>{r.emoji}</span>
            <span>{r.count} {r.count === 1 ? 'person' : 'people'}</span>
          </div>
          <div className="chat-reaction-tooltip-list">
            {names.map((n, i) => (
              <div key={i} className="chat-reaction-tooltip-item">{n}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── New Conversation Modal ──────────────────────────────────────────────────
function NewConvModal({ appId, api, myUserId, onCreated, onClose }) {
  const [type, setType] = useState('direct');
  const [groupName, setGroupName] = useState('');
  const [search, setSearch] = useState('');
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.listAppMembers(appId).then(r => {
      const rows = (r?.items || r || []).map(m => ({
        id:       m.user_id ?? m.id,
        name:     [m.first_name, m.last_name].filter(Boolean).join(' ') || m.name || m.email,
        email:    m.email,
        photo_url: m.photo_url || null,
      }));
      setMembers(rows.filter(m => String(m.id) !== String(myUserId)));
    }).catch(() => {});
  }, [appId, api, myUserId]);

  const filtered = members.filter(m =>
    !search || m.name?.toLowerCase().includes(search.toLowerCase()) || m.email?.toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (id) => {
    setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  };

  const handleCreate = async () => {
    if (!selected.length) return;
    if (type === 'group' && !groupName.trim()) return;
    setSaving(true);
    try {
      const conv = await api.chatCreateConv(appId, { type, name: groupName.trim() || null, member_ids: selected });
      onCreated(conv);
    } catch (e) {
      alert(e?.response?.data?.error || 'Failed to create conversation');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="chat-modal-overlay" onClick={onClose}>
      <div className="chat-modal" onClick={e => e.stopPropagation()}>
        <div className="chat-modal-header">
          <h3>New Conversation</h3>
          <button className="chat-modal-close" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="chat-modal-type-toggle">
          <button className={type === 'direct' ? 'active' : ''} onClick={() => { setType('direct'); setSelected([]); }}>
            <User size={14} /> Direct
          </button>
          <button className={type === 'group' ? 'active' : ''} onClick={() => { setType('group'); setSelected([]); }}>
            <Users size={14} /> Group
          </button>
        </div>

        {type === 'group' && (
          <input
            className="chat-modal-input"
            placeholder="Group name"
            value={groupName}
            onChange={e => setGroupName(e.target.value)}
          />
        )}

        <div className="chat-modal-search">
          <Search size={14} />
          <input placeholder="Search members…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>

        <div className="chat-modal-members">
          {filtered.map(m => (
            <label key={m.id} className={`chat-modal-member${selected.includes(m.id) ? ' selected' : ''}`}>
              <input
                type={type === 'direct' ? 'radio' : 'checkbox'}
                checked={selected.includes(m.id)}
                onChange={() => type === 'direct' ? setSelected([m.id]) : toggle(m.id)}
              />
              <div className="chat-avatar sm" data-initial={(m.name || m.email || '?')[0].toUpperCase()}>
                {m.photo_url
                  ? <img src={m.photo_url} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                  : (m.name || m.email || '?')[0].toUpperCase()}
              </div>
              <div>
                <div className="chat-member-name">{m.name || m.email}</div>
                {m.name && <div className="chat-member-email">{m.email}</div>}
              </div>
            </label>
          ))}
          {!filtered.length && <div className="chat-empty-small">No members found</div>}
        </div>

        <div className="chat-modal-footer">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={handleCreate} disabled={saving || !selected.length}>
            {saving ? 'Creating…' : 'Start Chat'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Edit Group Modal ────────────────────────────────────────────────────────
function EditGroupModal({ appId, api, myUserId, conv, onUpdated, onClose }) {
  const [groupName, setGroupName] = useState(conv.name || conv.display_name || '');
  const [search, setSearch] = useState('');
  const [allMembers, setAllMembers] = useState([]);
  const [checked, setChecked] = useState({});
  const [saving, setSaving] = useState(false);

  const existingIds = (conv.members || []).map(m => String(m.id));

  useEffect(() => {
    // include_inactive: a group member whose app membership was later
    // deactivated must still appear here with a checkbox, otherwise there's
    // no way to uncheck/remove them from the group at all.
    api.listAppMembers(appId, { includeInactive: true }).then(r => {
      const rows = (r?.items || r || [])
        .map(m => ({
          id:        String(m.user_id ?? m.id),
          name:      [m.first_name, m.last_name].filter(Boolean).join(' ') || m.name || m.email,
          email:     m.email,
          photo_url: m.photo_url || null,
        }))
        .filter(m => m.id !== String(myUserId));
      // Also cover a group member who has no app_memberships row at all
      // (fully removed platform user) — still show them so they can be removed.
      const rowIds = new Set(rows.map(m => m.id));
      const orphaned = (conv.members || [])
        .filter(m => String(m.id) !== String(myUserId) && !rowIds.has(String(m.id)))
        .map(m => ({ id: String(m.id), name: m.name, email: m.email, photo_url: m.photo_url || null }));
      const merged = [...rows, ...orphaned];
      setAllMembers(merged);
      // Pre-check existing members
      const init = {};
      merged.forEach(m => { init[m.id] = existingIds.includes(m.id); });
      setChecked(init);
    }).catch(() => {});
  }, [appId, api]);

  const filtered = allMembers.filter(m =>
    !search || m.name?.toLowerCase().includes(search.toLowerCase()) || m.email?.toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (id) => setChecked(s => ({ ...s, [id]: !s[id] }));

  const handleSave = async () => {
    if (!groupName.trim()) return;
    setSaving(true);
    try {
      const add_member_ids = allMembers.filter(m => checked[m.id] && !existingIds.includes(m.id)).map(m => Number(m.id));
      const remove_member_ids = allMembers.filter(m => !checked[m.id] && existingIds.includes(m.id)).map(m => Number(m.id));
      const updated = await api.chatEditGroup(appId, conv.id, {
        name: groupName.trim(),
        add_member_ids,
        remove_member_ids,
      });
      onUpdated(updated);
    } catch (e) {
      alert(e?.response?.data?.error || 'Failed to update group');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="chat-modal-overlay" onClick={onClose}>
      <div className="chat-modal" onClick={e => e.stopPropagation()}>
        <div className="chat-modal-header">
          <h3>Edit Group</h3>
          <button className="chat-modal-close" onClick={onClose}><X size={18} /></button>
        </div>

        <div style={{ padding: '12px 20px 0' }}>
          <label className="chat-modal-label">Group Name</label>
          <input
            className="chat-modal-input"
            style={{ margin: 0, width: '100%', boxSizing: 'border-box' }}
            placeholder="Group name"
            value={groupName}
            onChange={e => setGroupName(e.target.value)}
          />
        </div>

        <div style={{ padding: '10px 20px 4px', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>
          Members
        </div>

        <div className="chat-modal-search">
          <Search size={14} />
          <input placeholder="Search members…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>

        <div className="chat-modal-members">
          {filtered.map(m => (
            <label key={m.id} className={`chat-modal-member${checked[m.id] ? ' selected' : ''}`}>
              <input type="checkbox" checked={!!checked[m.id]} onChange={() => toggle(m.id)} />
              <div className="chat-avatar sm" data-initial={(m.name || m.email || '?')[0].toUpperCase()}>
                {m.photo_url
                  ? <img src={m.photo_url} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                  : (m.name || m.email || '?')[0].toUpperCase()}
              </div>
              <div>
                <div className="chat-member-name">{m.name || m.email}</div>
                {m.name && <div className="chat-member-email">{m.email}</div>}
              </div>
            </label>
          ))}
          {!filtered.length && <div className="chat-empty-small">No members found</div>}
        </div>

        <div className="chat-modal-footer">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving || !groupName.trim()}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Group Info Sidebar ───────────────────────────────────────────────────────
function GroupInfoSidebar({ appId, api, myUserId, conv, isGroupAdmin, onClose, onConvUpdated }) {
  const [members, setMembers] = useState(conv.members || []);
  const [busy, setBusy] = useState({});
  const [showAddParticipants, setShowAddParticipants] = useState(false);

  // Keep in sync when conv changes (e.g. socket update)
  useEffect(() => { setMembers(conv.members || []); }, [conv.members]);

  const adminCount = members.filter(m => m.is_admin).length;

  const handleToggleAdmin = async (member) => {
    if (!isGroupAdmin || String(member.id) === String(myUserId) || busy[member.id]) return;
    if (!member.is_admin && adminCount >= 5) {
      alert('Maximum 5 admins allowed per group');
      return;
    }
    setBusy(s => ({ ...s, [member.id]: true }));
    try {
      const res = await api.chatToggleAdmin(appId, conv.id, member.id);
      const updated = members.map(m => String(m.id) === String(member.id) ? { ...m, is_admin: res.is_admin } : m);
      setMembers(updated);
      onConvUpdated({ ...conv, members: updated });
    } catch (e) {
      alert(e?.response?.data?.error || 'Failed to update admin status');
    } finally {
      setBusy(s => ({ ...s, [member.id]: false }));
    }
  };

  const handleRemoveMember = async (member) => {
    if (!isGroupAdmin || String(member.id) === String(myUserId) || busy[member.id]) return;
    if (!window.confirm(`Remove ${member.name} from this group?`)) return;
    setBusy(s => ({ ...s, [member.id]: true }));
    try {
      const updated = await api.chatEditGroup(appId, conv.id, {
        name: conv.name || conv.display_name,
        remove_member_ids: [Number(member.id)],
      });
      onConvUpdated(updated);
    } catch (e) {
      alert(e?.response?.data?.error || 'Failed to remove member');
    } finally {
      setBusy(s => ({ ...s, [member.id]: false }));
    }
  };

  const createdAt = conv.created_at
    ? new Date(conv.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
    : null;

  return (
    <>
      <div className="group-info-sidebar">
        <div className="group-info-sidebar-header">
          <span>Group Info</span>
          <button className="chat-modal-close" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="group-info-avatar-wrap">
          <div className="chat-avatar lg" data-initial={(conv.display_name || 'G')[0].toUpperCase()}>
            {(conv.display_name || 'G')[0].toUpperCase()}
          </div>
          <div className="group-info-name">{conv.display_name}</div>
          {createdAt && <div className="group-info-meta">Created {createdAt}</div>}
          <div className="group-info-meta">{members.length} members · {adminCount} admin{adminCount !== 1 ? 's' : ''}</div>
        </div>

        <div className="group-info-section-label">Members</div>
        <div className="group-info-members">
          {members.map(m => {
            const isSelf = String(m.id) === String(myUserId);
            return (
              <div key={m.id} className="group-info-member-row">
                <div className="chat-avatar sm" data-initial={(m.name || '?')[0].toUpperCase()}>
                  {m.photo_url
                    ? <img src={m.photo_url} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                    : (m.name || '?')[0].toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="chat-member-name">{m.name}{isSelf ? ' (you)' : ''}</div>
                  {m.is_admin && <div className="group-info-admin-badge">Admin</div>}
                </div>
                {isGroupAdmin && !isSelf && (
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button
                      className={`chat-admin-toggle-btn${m.is_admin ? ' active' : ''}`}
                      disabled={!!busy[m.id]}
                      onClick={() => handleToggleAdmin(m)}
                      title={m.is_admin ? 'Remove admin' : 'Make admin'}
                    >
                      <Shield size={13} />
                      {busy[m.id] ? '…' : m.is_admin ? 'Remove' : 'Admin'}
                    </button>
                    <button
                      className="chat-member-remove-btn"
                      disabled={!!busy[m.id]}
                      onClick={() => handleRemoveMember(m)}
                      title="Remove from group"
                      aria-label="Remove from group"
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {isGroupAdmin && (
          <div style={{ padding: '12px 16px' }}>
            <button className="btn-primary" style={{ width: '100%' }} onClick={() => setShowAddParticipants(true)}>
              + Add Participants
            </button>
          </div>
        )}
      </div>

      {showAddParticipants && (
        <EditGroupModal
          appId={appId}
          api={api}
          myUserId={myUserId}
          conv={conv}
          onUpdated={(updated) => { setShowAddParticipants(false); onConvUpdated(updated); }}
          onClose={() => setShowAddParticipants(false)}
        />
      )}
    </>
  );
}

// ─── Delete Confirm Modal ────────────────────────────────────────────────────
function DeleteConfirmModal({ message, onConfirm, onCancel }) {
  return (
    <div className="chat-modal-overlay" onClick={onCancel}>
      <div className="chat-confirm-modal" onClick={e => e.stopPropagation()}>
        <p>{message || 'Are you sure?'}</p>
        <div className="chat-confirm-actions">
          <button className="btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="btn-danger" onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>
  );
}

// ─── Emoji Picker (for input) ────────────────────────────────────────────────
function EmojiPicker({ onSelect, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);
  return (
    <div className="chat-emoji-picker" ref={ref}>
      {ALL_EMOJIS.map(e => (
        <button key={e} className="chat-emoji-picker-btn" onClick={() => { onSelect(e); onClose(); }}>{e}</button>
      ))}
    </div>
  );
}

// ─── Tick component ──────────────────────────────────────────────────────────
function MessageTicks({ isRead, seenBy, isGroup }) {
  const [showSeenBy, setShowSeenBy] = useState(false);
  return (
    <span
      className={`chat-ticks${isRead ? ' read' : ''}`}
      title={isGroup && seenBy?.length ? `Seen by: ${seenBy.map(s => s.name).join(', ')}` : undefined}
      onClick={isGroup && seenBy?.length ? (e) => { e.stopPropagation(); setShowSeenBy(v => !v); } : undefined}
      style={isGroup && seenBy?.length ? { cursor: 'pointer' } : undefined}
    >
      {isRead ? (
        <>
          <Check size={10} strokeWidth={2.5} />
          <Check size={10} strokeWidth={2.5} className="chat-tick-second" />
        </>
      ) : (
        <Check size={10} strokeWidth={2.5} />
      )}
      {showSeenBy && seenBy?.length > 0 && (
        <span className="chat-seen-popup">
          <strong>Seen by</strong>
          {seenBy.map(s => <span key={s.id}>{s.name}</span>)}
        </span>
      )}
    </span>
  );
}

// ─── Forward Modal ───────────────────────────────────────────────────────────
function ForwardModal({ messages, conversations, noteConv, socket, appId, myUserId, api, onClose, onSent }) {
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [sending, setSending] = useState(false);
  const [allMembers, setAllMembers] = useState([]);
  const [limitWarning, setLimitWarning] = useState(false);

  useEffect(() => {
    api.listAppMembers(appId).then(r => {
      const rows = (r?.items || r || []).map(m => ({
        id:        m.user_id ?? m.id,
        name:      [m.first_name, m.last_name].filter(Boolean).join(' ') || m.name || m.email,
        email:     m.email,
        photo_url: m.photo_url || null,
      }));
      setAllMembers(rows.filter(m => String(m.id) !== String(myUserId)));
    }).catch(() => {});
  }, [appId, api, myUserId]);

  // Build unified target list:
  // 1. My Notes
  // 2. Existing group conversations
  // 3. Existing direct conversations (keyed by other member's user id)
  // 4. Members who don't yet have a direct conv — show as "member" targets so a conv is created on send
  const existingDirectByUserId = {};
  conversations.filter(c => c.type === 'direct').forEach(c => {
    const other = c.members?.find(m => String(m.id) !== String(myUserId));
    if (other) existingDirectByUserId[String(other.id)] = c;
  });

  const targets = [
    ...(noteConv ? [{ kind: 'conv', conv: { ...noteConv, display_name: 'My Notes' }, label: 'My Notes', photo_url: null, initial: '📝', isNote: true }] : []),
    ...conversations.filter(c => c.type === 'group').map(c => ({
      kind: 'conv', conv: c, label: c.display_name || 'Group', photo_url: null, initial: (c.display_name || 'G')[0].toUpperCase(), isNote: false,
    })),
    ...allMembers.map(m => {
      const existingConv = existingDirectByUserId[String(m.id)];
      return {
        kind: existingConv ? 'conv' : 'member',
        conv: existingConv || null,
        member: m,
        label: m.name || m.email,
        photo_url: m.photo_url,
        initial: (m.name || m.email || '?')[0].toUpperCase(),
        isNote: false,
      };
    }),
  ].filter(t => !search || t.label?.toLowerCase().includes(search.toLowerCase()) || t.member?.email?.toLowerCase().includes(search.toLowerCase()));

  // Each target's selection key: conv id or "member:<userId>"
  const targetKey = (t) => t.kind === 'conv' ? String(t.conv.id) : `member:${t.member.id}`;

  const FORWARD_RECIPIENT_LIMIT = 10;

  const toggle = (key) => {
    setSelected(prev => {
      if (!prev.has(key) && prev.size >= FORWARD_RECIPIENT_LIMIT) {
        setLimitWarning(true);
        return prev;
      }
      setLimitWarning(false);
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const sendToConv = async (s, convId) => {
    for (const msg of messages) {
      const body = (msg.body || '').replace(/\[\[att:\d+\]\]/g, '').trim();
      const attachments = (msg.attachments || []).map(a => ({
        file_key: a.file_key, file_url: a.file_url, file_name: a.file_name,
        file_size: a.file_size || null, content_type: a.content_type || null,
      }));
      await new Promise((resolve) => {
        s.emit('send_message', { conversation_id: convId, body: body || null, attachments, forwarded: true }, resolve);
        setTimeout(resolve, 300);
      });
    }
  };

  const handleSend = async () => {
    if (!selected.size || sending) return;
    const s = socket;
    if (!s?.connected) { alert('Not connected'); return; }
    setSending(true);
    try {
      for (const key of selected) {
        if (key.startsWith('member:')) {
          const userId = key.slice(7);
          const conv = await api.chatCreateConv(appId, { type: 'direct', member_ids: [userId] });
          // This socket's memberConvIds cache (populated at 'join' time) doesn't
          // know about a conversation created just now — send_message's server-side
          // membership check would silently reject every message for it otherwise,
          // which is why a forward to a brand-new recipient showed "No messages yet".
          s.emit('conv_joined', { conversation_id: conv.id });
          await sendToConv(s, conv.id);
        } else {
          await sendToConv(s, Number(key));
        }
      }
      onSent();
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="chat-modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="chat-modal" style={{ width: 360, maxHeight: '75vh', display: 'flex', flexDirection: 'column' }}>
        <div className="chat-modal-header">
          <span>Forward to</span>
          <button className="chat-modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <div style={{ padding: '8px 16px 4px' }}>
          <input
            className="form-input"
            style={{ width: '100%', boxSizing: 'border-box', fontSize: 13 }}
            placeholder="Search people or groups…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoFocus
          />
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}>
          {targets.map(t => {
            const key = targetKey(t);
            const checked = selected.has(key);
            return (
              <button
                key={key}
                onClick={() => toggle(key)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 16px', border: 'none', cursor: 'pointer',
                  color: 'var(--text-primary)', textAlign: 'left',
                  background: checked ? 'var(--bg-muted)' : 'transparent',
                }}
              >
                <div className="chat-avatar sm" data-initial={t.isNote ? '' : t.initial} style={{ flexShrink: 0 }}>
                  {t.isNote
                    ? '📝'
                    : t.photo_url
                      ? <img src={t.photo_url} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                      : t.initial}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.label}</div>
                  {t.kind === 'member' && t.member?.email && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.member.email}</div>
                  )}
                </div>
                <span style={{
                  width: 18, height: 18, borderRadius: 4, border: '2px solid var(--border)',
                  background: checked ? 'var(--accent)' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  borderColor: checked ? 'var(--accent)' : 'var(--border)',
                }}>
                  {checked && <Check size={11} color="#fff" />}
                </span>
              </button>
            );
          })}
          {!targets.length && (
            <div style={{ padding: '20px 16px', color: 'var(--text-muted)', fontSize: 13, textAlign: 'center' }}>
              No people or groups found
            </div>
          )}
        </div>
        {limitWarning && (
          <div style={{
            padding: '8px 16px', fontSize: 12, color: 'var(--danger, #e5484d)',
            background: 'var(--danger-bg, rgba(229, 72, 77, 0.1))', borderTop: '1px solid var(--border)',
          }}>
            You can only select up to {FORWARD_RECIPIENT_LIMIT} recipients at a time.
          </div>
        )}
        <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {selected.size ? `${selected.size} selected` : 'Select recipients'}
          </span>
          <button
            className="btn-primary"
            onClick={handleSend}
            disabled={!selected.size || sending}
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
          >
            <Send size={13} /> {sending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Conversation List Item ──────────────────────────────────────────────────
function ConvItem({ conv, active, onClick, isOnline, myUserId, typingUserIds = [], draft = '' }) {
  const last = conv.last_message;
  // A nudge's system message reads "X is calling your attention!" — that text
  // is only correct for the recipients. Since sender_id on it is now the
  // actual nudging user (not a generic system id), the nudger's own list can
  // tell it sent this one and show something that isn't backwards about
  // itself, even after a refresh with no live 'nudge' event to go on.
  const isOwnNudge = last?.message_type === 'system'
    && String(last.sender_id) === String(myUserId)
    && /is calling your attention/.test(last.body || '');
  const lastPreview = last
    ? (last.deleted_at
        ? 'This message was deleted'
        : isOwnNudge
          ? '🔔 You nudged the conversation'
          : (() => {
            const attCount = last.attachment_count || last.attachments?.length || 0;
            const cleaned = (last.body || '').replace(/\[\[att:\d+\]\]/g, '').replace(/@\[(\d+):([^\]]+)\]/g, '@$2').trim();
            if (cleaned && attCount) return (cleaned.slice(0, 40) + ' · 📎 ' + attCount).slice(0, 50);
            if (!cleaned && attCount) return `📎 ${attCount} attachment${attCount > 1 ? 's' : ''}`;
            return cleaned.length > 45 ? cleaned.slice(0, 45) + '…' : cleaned;
          })())
    : 'No messages yet';

  const isTyping = typingUserIds.length > 0;
  const typingLabel = (() => {
    if (!isTyping) return null;
    if (conv.type === 'direct') return 'typing…';
    const names = typingUserIds.map(uid => {
      const m = (conv.members || []).find(m => String(m.id) === String(uid));
      return m ? (m.name || m.first_name || 'Someone') : 'Someone';
    });
    return names.length === 1 ? `${names[0]} is typing…` : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]} are typing…`;
  })();
  const isRemoved = !!conv._removed;
  const hasDraft = !active && draft && draft.trim();
  const preview = isRemoved
    ? 'You are no longer a participant'
    : isTyping
      ? typingLabel
      : hasDraft
        ? null  // rendered separately with Draft: prefix
        : lastPreview;
  const initial = (conv.display_name || '?')[0].toUpperCase();
  const otherMember = conv.type === 'direct' ? conv.members?.find(m => String(m.id) !== String(myUserId)) : null;
  const photoUrl = conv.type === 'direct' ? (otherMember?.photo_url || null) : null;
  const timeStr = last?.created_at
    ? (() => {
        const d = new Date(last.created_at);
        const now = new Date();
        const isToday = d.toDateString() === now.toDateString();
        return isToday
          ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          : d.toLocaleDateString([], { day: '2-digit', month: 'short' });
      })()
    : null;

  return (
    <button className={`chat-conv-item${active ? ' active' : ''}`} onClick={onClick}>
      <div className="chat-avatar-wrap">
        <div className="chat-avatar" data-initial={initial}>
          {photoUrl ? <img src={photoUrl} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} /> : initial}
        </div>
        {isOnline && <span className="chat-online-dot" />}
      </div>
      <div className="chat-conv-info">
        <div className="chat-conv-name">
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{conv.display_name}</span>
          <span className="chat-conv-time">{timeStr}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4 }}>
          {hasDraft ? (
            <span className="chat-conv-draft-preview" style={{ flex: 1 }}>
              <span className="chat-conv-draft-label">Draft: </span>
              <span className="chat-conv-draft-text">{draft.length > 40 ? draft.slice(0, 40) + '…' : draft}</span>
            </span>
          ) : (
            <span className={`chat-conv-preview${isTyping ? ' chat-conv-typing' : ''}${isRemoved ? ' chat-conv-removed' : ''}`} style={{ flex: 1 }}>{preview}</span>
          )}
          {conv.unread_count > 0 && <span className="chat-unread-badge">{conv.unread_count}</span>}
        </div>
      </div>
    </button>
  );
}

const MSG_COLLAPSE_LIMIT = 300;

// ─── Message Bubble ──────────────────────────────────────────────────────────
function MessageBubble({ msg, isMine, memberReadAt, conv, myUserId, onDelete, onReply, onPin, onReact, onEdit, onForward, isForwardMode, isForwardSelected, onToggleForwardSelect, isGroupAdmin, showSender, sameSender, onRetry, onReplyClick, isHighlighted }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownFlip, setDropdownFlip] = useState(false);
  const [showReactionBar, setShowReactionBar] = useState(false);
  const [emojiGridStyle, setEmojiGridStyle] = useState({});
  const dropdownRef = useRef(null);
  const reactionBarRef = useRef(null);
  const plusBtnRef = useRef(null);
  const time = new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  // Close dropdown on outside click
  useEffect(() => {
    if (!showDropdown) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showDropdown]);

  // Close reaction bar on outside click
  useEffect(() => {
    if (!showReactionBar) return;
    const handler = (e) => {
      if (
        reactionBarRef.current && !reactionBarRef.current.contains(e.target) &&
        plusBtnRef.current && !plusBtnRef.current.contains(e.target)
      ) setShowReactionBar(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showReactionBar]);

  const otherMembers = (conv.members || []).filter(m => String(m.id) !== String(myUserId));
  const seenBy = otherMembers.filter(m => {
    const rAt = memberReadAt?.[m.id];
    return rAt && new Date(rAt) >= new Date(msg.created_at);
  });
  const isRead = seenBy.length > 0;
  const isGroup = conv.type === 'group';
  const isPinned = (conv.pinned_messages || []).some(p => p.id === msg.id);

  const isLong = !msg.deleted_at && msg.body && msg.body.length > MSG_COLLAPSE_LIMIT;

  return (
    <>
      <div
        className={`chat-msg-row${isMine ? ' mine' : ''}${isPinned ? ' pinned-msg' : ''}${sameSender ? ' same-sender' : ''}${isForwardMode ? ' forward-mode' : ''}${isForwardSelected ? ' forward-selected' : ''}${isHighlighted ? ' msg-highlighted' : ''}`}
        onClick={isForwardMode ? onToggleForwardSelect : undefined}
        style={isForwardMode ? { cursor: 'pointer' } : undefined}
        data-msg-id={msg.id}
      >
        {isForwardMode && (
          <span
            style={{
              width: 18, height: 18, borderRadius: 4, border: '2px solid var(--border)',
              background: isForwardSelected ? 'var(--accent)' : 'transparent',
              borderColor: isForwardSelected ? 'var(--accent)' : 'var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, alignSelf: 'center', marginRight: 4,
            }}
          >
            {isForwardSelected && <Check size={11} color="#fff" />}
          </span>
        )}
        {!isMine && (() => {
          const si = (msg.sender?.name || '?')[0].toUpperCase();
          const sPhoto = msg.sender?.photo_url;
          return showSender
            ? <div className="chat-avatar sm" data-initial={si}>
                {sPhoto ? <img src={sPhoto} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} /> : si}
              </div>
            : <div className="chat-avatar sm invisible" aria-hidden="true" />;
        })()}
        <div className="chat-bubble-wrap">
          {!isMine && showSender && <div className="chat-bubble-sender">{msg.sender?.name}</div>}

          {/* Forwarded label */}
          {msg.is_forwarded && !msg.deleted_at && (
            <div className="chat-forwarded-label">
              <Forward size={11} /> Forwarded
            </div>
          )}

          {/* Reply preview */}
          {msg.reply_to && !msg.deleted_at && (
            <div className="chat-reply-preview" style={{ cursor: 'pointer' }} onClick={() => onReplyClick?.(msg.reply_to.id)}>
              <span className="chat-reply-sender">{msg.reply_to.sender?.name || 'Unknown'}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <ReplyAttachmentPreview atts={replyAttachments(msg.reply_to)} />
                {replyBodyPreview(msg.reply_to) && <span className="chat-reply-body">{replyBodyPreview(msg.reply_to)}</span>}
              </div>
            </div>
          )}

          <div className={`chat-bubble${isMine ? ' mine' : ''}`}>
            {msg.deleted_at
              ? <em className="chat-deleted">This message was deleted</em>
              : renderMixedContent(
                  isLong && !expanded ? msg.body?.slice(0, MSG_COLLAPSE_LIMIT) + '…' : msg.body,
                  msg.attachments,
                  (seg) => renderWithLinks(seg)
                )
            }
            {!msg.deleted_at && msg.edited_at && (
              <span className="chat-edited-label">(edited)</span>
            )}
            {isLong && (
              <button className="chat-view-more" onClick={() => setExpanded(e => !e)}>
                {expanded ? 'View less' : 'View more'}
              </button>
            )}
            {!msg.deleted_at && !msg._tempId && (
              <div className={`chat-msg-actions${isMine ? ' mine' : ''}${showDropdown || showReactionBar ? ' force-visible' : ''}`} ref={dropdownRef}>
                <button className="chat-action-dropdown-btn" onClick={() => {
                  if (!showDropdown && dropdownRef.current) {
                    const rect = dropdownRef.current.getBoundingClientRect();
                    // dropdown is ~280px tall (emoji row + items); flip if less than 300px below
                    setDropdownFlip(window.innerHeight - rect.bottom < 300);
                  }
                  setShowDropdown(v => !v);
                }}>
                  <ChevronDown size={13} />
                </button>
                {showDropdown && (
                  <div className={`chat-dropdown-menu${isMine ? ' mine' : ''}${dropdownFlip ? ' flip-up' : ''}`}>
                    <div className="chat-dropdown-emoji-row">
                      {QUICK_EMOJIS.map(e => (
                        <button key={e} className="chat-reaction-quick-btn" onClick={() => { setShowDropdown(false); onReact(msg.id, e); }}>{e}</button>
                      ))}
                      <button
                        ref={plusBtnRef}
                        className="chat-reaction-quick-btn more"
                        onClick={() => {
                          if (!showReactionBar && plusBtnRef.current) {
                            const rect = plusBtnRef.current.getBoundingClientRect();
                            const gridHeight = 220;
                            const gridWidth = 258;
                            const spaceBelow = window.innerHeight - rect.bottom;
                            const top = spaceBelow >= gridHeight + 8
                              ? rect.bottom + 6
                              : rect.top - gridHeight - 6;
                            let left = rect.right - gridWidth;
                            if (left < 8) left = 8;
                            if (left + gridWidth > window.innerWidth - 8) left = window.innerWidth - gridWidth - 8;
                            setEmojiGridStyle({ top, left });
                          }
                          setShowReactionBar(v => !v);
                        }}
                        title="More reactions"
                      >＋</button>
                      {showReactionBar && (
                        <div className="chat-reaction-all" style={emojiGridStyle} ref={reactionBarRef}>
                          {ALL_EMOJIS.map(e => (
                            <button key={e} className="chat-emoji-picker-btn" onClick={() => { setShowReactionBar(false); setShowDropdown(false); onReact(msg.id, e); }}>{e}</button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="chat-dropdown-divider" />
                    <button className="chat-dropdown-item" onClick={() => { setShowDropdown(false); onReply(msg); }}>
                      <Reply size={13} /> Reply
                    </button>
                    <button className="chat-dropdown-item" onClick={() => { setShowDropdown(false); onForward(msg); }}>
                      <Forward size={13} /> Forward
                    </button>
                    {!msg.deleted_at && (() => { const t = (msg.body || '').replace(/\[\[(?:att|id):\d+\]\]/g, '').replace(/@\[(\d+):([^\]]+)\]/g, '@$2').trim(); return t ? (
                      <button className="chat-dropdown-item" onClick={() => {
                        setShowDropdown(false);
                        navigator.clipboard.writeText(t).catch(() => {});
                      }}>
                        <Copy size={13} /> Copy
                      </button>
                    ) : null; })()}
                    {!msg.deleted_at && msg.attachments?.length > 0 && (
                      <button className="chat-dropdown-item" onClick={async () => {
                        setShowDropdown(false);
                        const atts = msg.attachments;
                        if (atts.length === 1) {
                          try {
                            const res = await fetch(atts[0].file_url);
                            const blob = await res.blob();
                            const blobUrl = URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = blobUrl;
                            link.download = atts[0].file_name || 'attachment';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
                          } catch { window.open(atts[0].file_url, '_blank'); }
                        } else {
                          try {
                            const zip = new JSZip();
                            const seen = {};
                            await Promise.all(atts.map(async (a) => {
                              const res = await fetch(a.file_url);
                              const blob = await res.blob();
                              let name = a.file_name || 'attachment';
                              if (seen[name]) {
                                seen[name]++;
                                const ext = name.includes('.') ? name.slice(name.lastIndexOf('.')) : '';
                                const base = name.slice(0, name.length - ext.length);
                                name = `${base}(${seen[name]})${ext}`;
                              } else { seen[name] = 1; }
                              zip.file(name, blob);
                            }));
                            const zipBlob = await zip.generateAsync({ type: 'blob' });
                            const blobUrl = URL.createObjectURL(zipBlob);
                            const link = document.createElement('a');
                            link.href = blobUrl;
                            link.download = 'Attachments.zip';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
                          } catch { atts.forEach(a => window.open(a.file_url, '_blank')); }
                        }
                      }}>
                        <Download size={13} /> Download
                      </button>
                    )}
                    {isMine && (
                      <button className="chat-dropdown-item danger" onClick={() => { setShowDropdown(false); setConfirmDelete(true); }}>
                        <Trash2 size={13} /> Delete
                      </button>
                    )}
                    <button className="chat-dropdown-item" onClick={() => { setShowDropdown(false); onPin(msg); }}>
                      <Pin size={13} /> {isPinned ? 'Unpin' : 'Pin'}
                    </button>
                    {isMine && (
                      <button className="chat-dropdown-item" onClick={() => { setShowDropdown(false); onEdit(msg); }}>
                        <Edit2 size={13} /> Edit
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="chat-bubble-time">
            {isPinned && <Pin size={9} style={{ opacity: 0.6, marginRight: 2 }} />}
            {msg.status === 'sending' && <span className="chat-msg-sending-dot" title="Sending…" />}
            {msg.status !== 'sending' && msg.status !== 'failed' && time}
            {msg.status === 'failed' && (
              <>
                <span style={{ fontSize: 10, color: 'var(--error, #ef4444)', marginRight: 2 }}>Not sent</span>
                {time}
              </>
            )}
            {isMine && !msg.deleted_at && msg.status !== 'sending' && msg.status !== 'failed' && (
              <MessageTicks isRead={isRead} seenBy={isGroup ? seenBy : []} isGroup={isGroup} />
            )}
            {msg.status === 'failed' && <FailedIcon onRetry={onRetry} />}
          </div>

          {/* Reactions display — inside bubble-wrap so it sits below the bubble */}
          {(msg.reactions || []).length > 0 && (
            <div className={`chat-reactions-row${isMine ? ' mine' : ''}`}>
              {msg.reactions.map(r => {
                const iReacted = r.user_ids?.includes(myUserId) || r.user_ids?.map(String).includes(String(myUserId));
                const names = r.user_names?.length ? r.user_names : [];
                return (
                  <ReactionPill
                    key={r.emoji}
                    r={r}
                    iReacted={iReacted}
                    names={names}
                    onReact={() => onReact(msg.id, r.emoji)}
                  />
                );
              })}
            </div>
          )}
        </div>


      </div>

      {confirmDelete && (
        <DeleteConfirmModal
          message="Delete this message?"
          onConfirm={() => { setConfirmDelete(false); onDelete(msg.id); }}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </>
  );
}

// ─── Failed message status icon ─────────────────────────────────────────────
function FailedIcon({ onRetry }) {
  return (
    <button
      className="chat-msg-failed-btn"
      onClick={onRetry}
      title="Failed to send — tap to retry"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
    </button>
  );
}

// ─── Chat Window ─────────────────────────────────────────────────────────────
function replyBodyPreview(msg) {
  if (!msg) return '';
  if (msg.deleted_at) return 'This message was deleted';
  const cleaned = (msg.body || '')
    .replace(/\[\[(?:att|id):\d+\]\]/g, '')
    .replace(/@\[(\d+):([^\]]+)\]/g, '@$2')
    .trim();
  if (cleaned) return cleaned.slice(0, 80);
  return '';
}

// Returns the replied-to message's attachments for preview rendering
function replyAttachments(msg) {
  if (!msg || msg.deleted_at) return [];
  return msg.attachments || [];
}

// Renders the attachment portion of a reply preview inline. Shows the single
// attachment (image/video thumbnail or file chip) when there's exactly one;
// otherwise shows a count so multi-attachment replies aren't silently reduced
// to just the first file's name.
function ReplyAttachmentPreview({ atts }) {
  if (!atts || atts.length === 0) return null;
  if (atts.length > 1) {
    return (
      <span className="chat-reply-file-chip">
        <FileText size={11} />
        <span className="chat-reply-file-name">{atts.length} Attachments</span>
      </span>
    );
  }
  const att = atts[0];
  const ct = att.content_type || '';
  const isImg = ct.startsWith('image/') || /\.(png|jpe?g|gif|webp|svg)$/i.test(att.file_name || '');
  const isVid = ct.startsWith('video/');
  if (isImg) {
    return (
      <img
        src={att.file_url}
        alt={att.file_name}
        style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
      />
    );
  }
  if (isVid) {
    return (
      <video
        src={`${att.file_url}#t=0.1`}
        preload="metadata"
        muted
        playsInline
        style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
      />
    );
  }
  // File chip
  return (
    <span className="chat-reply-file-chip">
      <FileText size={11} />
      <span className="chat-reply-file-name">{att.file_name || 'File'}</span>
    </span>
  );
}

// ─── Retry backoff (bounded exponential + jitter) ────────────────────────────
// Applies to every client-side retry trigger for a stuck/failed message:
// reconnect catch-up, the 'online' event, and the periodic sweep. Prevents a
// retry storm when many messages are pending (e.g. a large offline backlog
// reconnecting at once, or a sustained server-side outage) by spacing out
// each message's own retry attempts instead of blasting every trigger
// through unconditionally. Base delay matches the backend's own
// BullMQ/local-queue convention (2s) for consistency; capped so a
// long-failing message doesn't end up waiting minutes between auto-retries.
const RETRY_BASE_DELAY_MS = 2000;
const RETRY_MAX_DELAY_MS = 60000;
const RETRY_JITTER_RATIO = 0.2; // +/-20%

function computeBackoffDelayMs(attempts) {
  const raw = Math.min(RETRY_MAX_DELAY_MS, RETRY_BASE_DELAY_MS * Math.pow(2, Math.max(0, attempts - 1)));
  const jitter = raw * RETRY_JITTER_RATIO * (Math.random() * 2 - 1); // uniform in [-ratio, +ratio]
  return Math.max(RETRY_BASE_DELAY_MS, Math.round(raw + jitter));
}

// Draft helpers
// ─── Outbox persistence (survives refresh) ───────────────────────────────────
// Backed by IndexedDB (see lib/chatOutbox.js) instead of localStorage — the
// approved chat redesign requires this specifically: localStorage is
// synchronous, whole-blob-per-write, and quota-limited, which made it a poor
// fit for "the durable record of a pending send" under burst-send load. Same
// per-conversation semantics as before (add/remove/load by clientMessageId);
// callers below just need to await these now since IndexedDB is async.

const draftKey = (appId, convId) => `chat_draft_${appId}_${convId}`;
function loadDraft(appId, convId) {
  try { return localStorage.getItem(draftKey(appId, convId)) || ''; } catch { return ''; }
}
function saveDraft(appId, convId, text) {
  try {
    const trimmed = text.trim();
    if (trimmed) localStorage.setItem(draftKey(appId, convId), trimmed);
    else localStorage.removeItem(draftKey(appId, convId));
  } catch {}
}
function clearDraft(appId, convId) {
  try { localStorage.removeItem(draftKey(appId, convId)); } catch {}
}

function ChatWindow({ conv, appId, api, myUserId, socket, onConvUpdated, onConvDeleted, onlineUserIds = [], conversations, noteConv, onRemovedFromGroup, onDraftChange }) {
  const { getToken } = useAuth();
  const [messages, setMessages] = useState([]);
  // Durable outbox availability — checked once per mount. When false (private
  // browsing restrictions, quota errors, IndexedDB disabled), sending still
  // works within this session (the existing in-memory failedQueue/retry
  // machinery is unaffected), but a message pending at refresh time will not
  // survive it. This is surfaced explicitly rather than silently degrading,
  // per the approved design: never represent a message as durably queued
  // when it isn't.
  const [outboxDurable, setOutboxDurable] = useState(true);
  useEffect(() => {
    let cancelled = false;
    isOutboxDurable().then((durable) => { if (!cancelled) setOutboxDurable(durable); });
    return () => { cancelled = true; };
  }, []);
  // Last-resort safety net: whatever upstream reconciliation path produced a
  // second entry for the same message (offline burst + reconnect races
  // between the catch-up fetch, message_saved broadcast, and HTTP retry
  // queue proved hard to fully eliminate at the source), collapse it here so
  // the rendered list can never show a visible duplicate bubble. Keyed by id
  // when confirmed, falling back to _tempId while still pending — keeps the
  // LAST occurrence, since a later entry is the more up-to-date one.
  const dedupedMessages = useMemo(() => {
    // A confirmed message loses its _tempId once an upstream handler converts
    // it (client_message_id: undefined, _tempId: undefined — see the
    // message_saved handler), so a ghost bubble that a *different* handler
    // failed to remove in the same pass ends up keyed differently from its
    // own confirmed twin (`tmp:X` vs `id:Y`) and survives this dedup even
    // though it's the same underlying message. Two passes: first collapse
    // by id (confirmed rows win), then drop any still-optimistic ghost whose
    // tempId matches a message that already has a real id under any key.
    const byId = new Map();
    const tempOnly = [];
    for (const m of messages) {
      if (m.id != null) {
        byId.set(m.id, m);
      } else {
        tempOnly.push(m);
      }
    }
    const confirmedTempIds = new Set();
    for (const m of byId.values()) {
      if (m._tempId) confirmedTempIds.add(m._tempId);
      if (m.client_message_id) confirmedTempIds.add(m.client_message_id);
    }
    const seenTemp = new Map();
    for (const m of tempOnly) {
      if (m._tempId && confirmedTempIds.has(m._tempId)) continue; // already confirmed elsewhere — drop the ghost
      seenTemp.set(m._tempId ?? Symbol(), m);
    }
    return [...byId.values(), ...seenTemp.values()].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at)
    );
  }, [messages]);
  const [loading, setLoading] = useState(true);
  const [text, _setText] = useState('');
  const textRef = useRef(''); // mirrors text for unmount cleanup
  const setText = useCallback((v) => { const s = typeof v === 'function' ? v(textRef.current) : v; textRef.current = s; _setText(s); }, []);
  const [hasMore, setHasMore] = useState(true);
  const messagesStateRef = useRef([]);
  useEffect(() => { messagesStateRef.current = messages; }, [messages]);
  const [memberReadAt, setMemberReadAt] = useState({});
  const [replyTo, setReplyTo] = useState(null);
  const [fileErrors, setFileErrors] = useState([]);
  const [forwardMode, setForwardMode] = useState(false);
  const [forwardSelected, setForwardSelected] = useState(new Set());
  const [forwardLimitWarning, setForwardLimitWarning] = useState(false);
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [removedFromGroup, setRemovedFromGroup] = useState(!!conv._removed);
  const [removedByName, setRemovedByName] = useState(null);
  const [typingUsers, setTypingUsers] = useState([]);
  const [typingRotateIdx, setTypingRotateIdx] = useState(0);
  const typingRotateTimer = useRef(null);
  const typingTimers = useRef({});
  const fileInputRef = useRef(null);
  const composeRef = useRef(null);   // contenteditable div
  const savedRange = useRef(null);   // saved caret range before file dialog steals focus
  const { uploadFiles, uploading } = useSpacesUpload(appId, 'chat');
  const convId = conv.id;

  // All workspace members for @ mention (loaded once per appId) — used for
  // direct chats, where mentioning is not limited to the other participant.
  const [allMembers, setAllMembers] = useState([]);
  useEffect(() => {
    api.listAppMembers(appId).then(r => {
      const rows = (r?.items || r || [])
        .filter(m => String(m.user_id ?? m.id) !== String(myUserId))
        .map(m => {
          const parts = [m.first_name, m.last_name].filter(Boolean).join(' ') || m.name || '';
          const nameParts = parts.trim().split(' ');
          return { user_id: m.user_id ?? m.id, first_name: nameParts[0] || '', last_name: nameParts.slice(1).join(' ') || '', email: m.email || '' };
        });
      setAllMembers(rows);
    }).catch(() => {});
  }, [appId, api, myUserId]);
  // In a group, only members of that group should be mentionable.
  const mentionCandidates = conv.type === 'group'
    ? (conv.members || [])
        .filter(m => String(m.id) !== String(myUserId))
        .map(m => {
          const parts = (m.name || '').trim().split(' ');
          return { user_id: m.id, first_name: parts[0] || '', last_name: parts.slice(1).join(' ') || '', email: m.email || '' };
        })
    : allMembers;
  const { handleMentionKeyDown, handleMentionInput, closeMention, MentionDropdown } = useMentionInContentEditable(composeRef, mentionCandidates);

  // Restore draft on mount, save on unmount
  useEffect(() => {
    const draft = loadDraft(appId, convId);
    if (draft && composeRef.current) {
      composeRef.current.innerText = draft;
      // move caret to end
      const el = composeRef.current;
      const r = document.createRange();
      r.selectNodeContents(el);
      r.collapse(false);
      const sel = window.getSelection();
      if (sel) { sel.removeAllRanges(); sel.addRange(r); }
      setText(draft);
    }
    return () => {
      const currentText = textRef.current || '';
      saveDraft(appId, convId, currentText);
      if (onDraftChange) onDraftChange(convId, currentText.trim());
    };
    // Only run on mount/unmount for this convId
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save caret so we can restore it after file dialog returns focus
  const saveRange = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && composeRef.current?.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  };

  // Restore saved caret (or place at end)
  const restoreRange = () => {
    const el = composeRef.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    if (savedRange.current) {
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    } else {
      // move caret to end
      const r = document.createRange();
      r.selectNodeContents(el);
      r.collapse(false);
      sel.removeAllRanges();
      sel.addRange(r);
    }
  };

  // ── helpers for the rich file chip ──────────────────────────────────────────
  function chatFileEmoji(name, contentType) {
    const ext = (name || '').split('.').pop()?.toLowerCase() || '';
    const ct = contentType || '';
    if (ct.includes('zip') || ['zip', 'rar', '7z', 'tar', 'gz', 'tgz', 'tbz2', 'bz2', 'xz', 'zst'].includes(ext)) return '🗜️';
    if (ct.includes('spreadsheet') || ct.includes('excel') || ['xls', 'xlsx', 'csv', 'tsv'].includes(ext)) return '📊';
    if (ct === 'application/pdf' || ext === 'pdf') return '📕';
    if (['doc', 'docx', 'odt', 'rtf'].includes(ext)) return '📝';
    if (['ppt', 'pptx'].includes(ext)) return '📽️';
    if (['mobi', 'epub'].includes(ext)) return '📖';
    if (['obj', 'stl', 'gltf', 'glb', 'ply', 'step', 'iges', '3mf', 'dxf'].includes(ext)) return '🧊';
    if (['ttf', 'otf', 'woff', 'woff2', 'eot'].includes(ext)) return '🔤';
    if (['log', 'accesslog', 'syslog', 'trace'].includes(ext)) return '🧾';
    if (['json', 'xml', 'yaml', 'yml', 'sql', 'jsonl', 'ndjson', 'toml', 'ini'].includes(ext)) return '🗄️';
    if (['js', 'css', 'html', 'py', 'java', 'go', 'php', 'rb', 'rs', 'sh', 'c', 'cpp'].includes(ext)) return '💻';
    if (ct.startsWith('image/') || ['tiff', 'tif', 'heic', 'heif', 'bmp', 'psd', 'eps', 'dcm', 'nii', 'avif', 'ico', 'svg'].includes(ext)) return '🖼️';
    return '📎';
  }
  function chatFormatFileSize(bytes) {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  // Insert image thumbnail node(s) at current caret inside contenteditable
  const insertFilesAtCaret = (files) => {
    const el = composeRef.current;
    if (!el) return;
    restoreRange();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    files.forEach((file, fi) => {
      const wrapper = document.createElement('span');
      wrapper.contentEditable = 'false';
      wrapper.className = 'chat-inline-img-wrap';
      wrapper.dataset.fileName = file.name;
      wrapper.dataset.fileType = file.type;
      // store file on element directly
      wrapper._file = file;
      const BROWSER_RENDERABLE_IMAGE = /^image\/(jpeg|png|gif|webp|svg\+xml|bmp|avif)$/;
      if (file.type.startsWith('image/') && BROWSER_RENDERABLE_IMAGE.test(file.type)) {
        const url = URL.createObjectURL(file);
        const img = document.createElement('img');
        img.src = url;
        img.className = 'chat-inline-img-thumb';
        img.alt = file.name;
        img.onload = () => URL.revokeObjectURL(url);
        wrapper.appendChild(img);
        const removeBtn = document.createElement('button');
        removeBtn.className = 'chat-inline-img-remove';
        removeBtn.textContent = '×';
        removeBtn.type = 'button';
        removeBtn.onclick = () => wrapper.remove();
        wrapper.appendChild(removeBtn);
      } else {
        const chip = document.createElement('span');
        chip.className = 'chat-inline-file-chip';

        const iconSpan = document.createElement('span');
        iconSpan.className = 'chat-inline-file-chip-icon';
        iconSpan.textContent = chatFileEmoji(file.name, file.type);
        chip.appendChild(iconSpan);

        const body = document.createElement('span');
        body.className = 'chat-inline-file-chip-body';
        const nameSpan = document.createElement('span');
        nameSpan.className = 'chat-inline-file-chip-name';
        const shortName = file.name.length > 20 ? file.name.slice(0, 17) + '…' : file.name;
        nameSpan.textContent = shortName;
        nameSpan.title = file.name;
        body.appendChild(nameSpan);
        const sizeSpan = document.createElement('span');
        sizeSpan.className = 'chat-inline-file-chip-size';
        sizeSpan.textContent = chatFormatFileSize(file.size);
        body.appendChild(sizeSpan);
        chip.appendChild(body);

        const removeBtn = document.createElement('button');
        removeBtn.className = 'chat-inline-file-chip-remove';
        removeBtn.textContent = '×';
        removeBtn.type = 'button';
        removeBtn.title = 'Remove file';
        removeBtn.onclick = () => wrapper.remove();
        chip.appendChild(removeBtn);

        wrapper.appendChild(chip);
      }
      // insert at caret, then move caret after it
      range.insertNode(wrapper);
      range.setStartAfter(wrapper);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    });
    // ensure there's a text node after last insert so user can keep typing
    const afterNode = document.createTextNode('');
    range.insertNode(afterNode);
    range.setStart(afterNode, 0);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
    syncTextFromCompose();
  };

  // Keep `text` state in sync for char count / send button disabled check
  const typingStopTimer = useRef(null);
  const syncTextFromCompose = () => {
    const el = composeRef.current;
    if (!el) return;
    handleMentionInput();
    setText(getComposeText(el));
    // Emit typing indicator
    const s = socket || getSocket();
    if (s?.connected && socketJoined.current) {
      s.emit('typing', { conversation_id: convId, is_typing: true });
      clearTimeout(typingStopTimer.current);
      typingStopTimer.current = setTimeout(() => {
        s.emit('typing', { conversation_id: convId, is_typing: false });
      }, 2000);
    }
  };

  // Walk compose DOM and extract plain text (ignoring image nodes)
  const getComposeText = (el) => {
    let out = '';
    el.childNodes.forEach(node => {
      if (node.nodeType === Node.TEXT_NODE) {
        out += node.textContent;
      } else if (node.nodeName === 'BR') {
        out += '\n';
      } else if (node.nodeName === 'DIV') {
        out += '\n' + getComposeText(node);
      } else if (node.dataset?.mentionId) {
        out += node.textContent;
      } else if (node.dataset?.fileName) {
        // image/file node — skip
      } else {
        out += node.textContent;
      }
    });
    return out;
  };

  // Walk compose DOM in order → return { bodyWithMarkers, files }
  const extractComposeData = () => {
    const el = composeRef.current;
    if (!el) return { bodyWithMarkers: '', files: [] };
    const files = [];
    let body = '';
    const walk = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        body += node.textContent;
      } else if (node.nodeName === 'BR') {
        body += '\n';
      } else if (node.nodeName === 'DIV') {
        body += '\n';
        node.childNodes.forEach(walk);
      } else if (node.dataset?.mentionId) {
        body += `@[${node.dataset.mentionId}:${node.dataset.mentionName || node.textContent.slice(1)}]`;
      } else if (node._file instanceof File) {
        const idx = files.length;
        files.push(node._file);
        if (body.length > 0 && !body.endsWith('\n')) body += '\n';
        body += `[[att:${idx}]]`;
      } else {
        node.childNodes.forEach(walk);
      }
    };
    el.childNodes.forEach(walk);
    return { bodyWithMarkers: body.trim(), files };
  };

  const addFiles = (incoming) => {
    const { valid, errors } = validateFiles(incoming);
    setFileErrors(errors);
    if (!valid.length) return;
    insertFilesAtCaret(valid);
  };

  useEffect(() => {
    const input = fileInputRef.current;
    if (!input) return;
    const handler = (e) => {
      const files = Array.from(e.target.files || []);
      if (files.length) addFiles(files);
      e.target.value = '';
    };
    input.addEventListener('change', handler);
    return () => input.removeEventListener('change', handler);
  }, []);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showEditGroup, setShowEditGroup] = useState(false);
  const [showGroupInfo, setShowGroupInfo] = useState(false);
  const [confirmDeleteGroup, setConfirmDeleteGroup] = useState(false);
  const [pinnedMessages, setPinnedMessages] = useState(conv.pinned_messages || []);
  const [pinnedIndex, setPinnedIndex] = useState(0);
  const [editingMsg, setEditingMsg] = useState(null);
  const [editingMsgAtts, setEditingMsgAtts] = useState([]);
  const [nudging, setNudging] = useState(false);         // shake on sender side
  const [nudgeShake, setNudgeShake] = useState(false);   // shake on receiver side
  const [muted, setMuted] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`chat_mute_${conv.id}`) || 'false'); } catch { return false; }
  });
  // Sync mute state when switching conversations (conv.id changes but component may remount via key anyway)
  useEffect(() => {
    try { setMuted(JSON.parse(localStorage.getItem(`chat_mute_${conv.id}`) || 'false')); } catch {}
  }, [conv.id]);
  const [socketConnected, setSocketConnected] = useState(true);
  const [highlightedMsgId, setHighlightedMsgId] = useState(null);
  const socketJoined = useRef(false);
  const pendingMsg = useRef(null);
  // failed outbox: tempId → { body, reply_to_id, attachments, _tempId, created_at }
  const failedQueue = useRef({});
  // Per-message retry backoff state: tempId → { attempts, nextEligibleAt }.
  // Bounded exponential backoff with jitter — required so a burst of retry
  // triggers (reconnect, the 'online' event, the periodic 10s sweep) firing
  // close together for the same stuck message doesn't turn into a retry
  // storm. Transient scheduling state only, not outbox durability — cleared
  // whenever a message actually succeeds or is manually retried.
  const retryBackoff = useRef({});
  const sendTimeouts = useRef({});
  // inFlight: tempId → true while a POST is in-flight; prevents duplicate concurrent requests
  const inFlight = useRef({});
  // Buffer for message_saved events — processed in a useEffect to avoid flushSync re-entrancy
  const savedQueue = useRef([]);
  // Serial send queue — ensures messages are sent one at a time, in order (socket path)
  const sendQueue = useRef([]);
  const sendQueueRunning = useRef(false);
  // HTTP send queue — serializes HTTP POSTs so the server isn't overwhelmed
  const httpSendQueue = useRef([]);
  const httpSendRunning = useRef(false);
  const bottomRef = useRef(null);
  const messagesRef = useRef(null);
  const isGroup = conv.type === 'group';
  const isGroupAdmin = !!conv.is_group_admin;

  // Keep pinnedMessages in sync when conv prop changes. Seed from the
  // (possibly stale) conv.pinned_messages immediately for a fast first
  // paint, then re-fetch fresh from the server — the parent's cached
  // conversations list can be out of date if a message_pinned broadcast
  // was missed (e.g. a brief socket disconnect right as someone pinned).
  useEffect(() => {
    setPinnedMessages(conv.pinned_messages || []);
    setPinnedIndex(0);
    api.chatPinnedMessages(appId, conv.id)
      .then(({ pinned_messages }) => setPinnedMessages(pinned_messages || []))
      .catch(() => {});
  }, [conv.id]);

  const loadMessages = useCallback(async (before = null, after = null) => {
    try {
      const params = { limit: 10 };
      if (before) params.before = before;
      if (after) params.after = after;
      const msgs = await api.chatMessages(appId, convId, params);
      if (before) {
        // Loading older messages (pagination)
        setMessages(prev => [...msgs, ...prev]);
        setHasMore(msgs.length === 10);
        return msgs;
      } else if (after) {
        // Catching up after reconnect — append only new messages, don't touch hasMore
        if (msgs.length > 0) {
          setMessages(prev => {
            const existingIds = new Set(prev.map(m => m.id));
            // Also collect tempIds of optimistic messages still in state
            const existingTempIds = new Set(prev.map(m => m._tempId).filter(Boolean));
            const newMsgs = msgs.filter(m => {
              if (existingIds.has(m.id)) return false;
              // If an optimistic with matching client_message_id is already in state,
              // replace it instead of adding a duplicate (prevents ghost + real pair).
              if (m.client_message_id && existingTempIds.has(m.client_message_id)) return false;
              return true;
            });
            // For any caught-up message that matches an in-flight optimistic, replace it
            const replacements = {};
            msgs.forEach(m => {
              if (m.client_message_id && existingTempIds.has(m.client_message_id)) {
                replacements[m.client_message_id] = m;
                removeFromOutbox(appId, convId, m.client_message_id);
                delete failedQueue.current[m.client_message_id];
              }
            });
            const merged = prev.map(m =>
              m._tempId && replacements[m._tempId]
                ? { ...replacements[m._tempId], status: 'sent', _tempId: m._tempId }
                : m
            );
            return newMsgs.length > 0 ? [...merged, ...newMsgs] : merged;
          });
          setTimeout(() => {
            const el = messagesRef.current;
            if (el) el.scrollTop = el.scrollHeight;
          }, 50);
        }
        // Return the raw server response (not the merged React state) — it has
        // real ids straight from Postgres and is available synchronously here,
        // unlike `messages` state or messagesStateRef, which only update after
        // React commits and its mirroring effect runs. Callers that need the
        // latest message id right after this resolves (markRead) should use
        // this return value instead of racing React's render/effect timing.
        return msgs;
      } else {
        // Initial load — merge server messages with any outbox items already in state.
        // Do NOT set status:'sending' here; the restore useEffect below owns that.
        setMessages(prev => {
          const optimistic = prev.filter(m => m._tempId);
          // Deduplicate: if a server message matches an optimistic tempId, drop optimistic
          const serverTempIds = new Set(msgs.map(m => m.client_message_id).filter(Boolean));
          const stillPending = optimistic.filter(m => !serverTempIds.has(m._tempId));
          // Also remove from outbox any that the server already has
          optimistic.forEach(m => {
            if (serverTempIds.has(m._tempId)) {
              removeFromOutbox(appId, convId, m._tempId);
              delete failedQueue.current[m._tempId];
            }
          });
          return [...msgs, ...stillPending];
        });
        setHasMore(msgs.length === 10);
        const seedMap = {};
        (conv.members || []).forEach(m => {
          if (String(m.id) !== String(myUserId) && m.last_read_at) {
            seedMap[m.id] = m.last_read_at;
          }
        });
        setMemberReadAt(seedMap);
        setTimeout(() => {
          const el = messagesRef.current;
          if (el) el.scrollTop = el.scrollHeight;
        }, 50);
        // See the comment on the `after` branch's return above — same reasoning.
        return msgs;
      }
    } catch (e) {
      console.error('load messages', e);
    } finally {
      setLoading(false);
    }
  }, [api, appId, convId, conv.members, myUserId]);

  // Keep a stable ref so the init effect below doesn't re-fire when loadMessages
  // gets a new reference (e.g. conv.members updates on every new message received).
  const loadMessagesRef = useRef(loadMessages);
  useEffect(() => { loadMessagesRef.current = loadMessages; }, [loadMessages]);
  const sendViaHttpRef = useRef(null);
  const drainHttpSendQueueRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    setMessages([]);
    setHasMore(true);
    setReplyTo(null);
    loadMessagesRef.current();
    // Only re-run when the conversation actually changes, not when loadMessages
    // gets a new reference due to conv.members updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convId]);

  // Restore unsent messages from the IndexedDB outbox on mount / conversation
  // switch. Show as 'failed' so the red retry indicator is visible
  // immediately — the send never completed (or was never confirmed) if
  // they're still in the outbox. loadOutbox is async (IndexedDB), so this
  // effect body is wrapped in an IIFE; the effect itself stays synchronous.
  useEffect(() => {
    let cancelled = false;
    (async () => {
    const pending = await loadOutbox(appId, convId);
    if (cancelled) return;
    if (!pending.length) return;
    const optimisticMsgs = pending.map(item => {
      failedQueue.current[item._tempId] = item;
      return {
        _tempId: item._tempId,
        id: null,
        conversation_id: convId,
        sender_id: myUserId,
        sender: { id: myUserId, name: (conv.members || []).find(m => String(m.id) === String(myUserId))?.name || 'You' },
        body: item.body,
        attachments: item.attachments || [],
        reply_to: null,
        created_at: item.created_at || new Date().toISOString(),
        status: 'failed',
      };
    });
    setMessages(prev => {
      const existingTempIds = new Set(prev.map(m => m._tempId).filter(Boolean));
      const existingClientIds = new Set(prev.map(m => m.client_message_id).filter(Boolean));
      const fresh = optimisticMsgs.filter(m =>
        !existingTempIds.has(m._tempId) && !existingClientIds.has(m._tempId)
      );
      // Also clean up failedQueue for items already confirmed in DB
      optimisticMsgs.forEach(m => {
        if (!fresh.includes(m)) delete failedQueue.current[m._tempId];
      });
      return [...prev, ...fresh];
    });
    // Immediately retry via queue — serialized so server isn't overwhelmed.
    // Skip while genuinely offline; the 'online' event listener elsewhere
    // covers retrying once connectivity actually returns.
    setTimeout(() => {
      if (!navigator.onLine) return;
      pending.forEach(item => {
        const tempId = item._tempId;
        // Skip if socket already confirmed this message while we were waiting
        if (!failedQueue.current[tempId]) return;
        if (inFlight.current[tempId] || httpSendQueue.current.some(q => q.tempId === tempId)) return;
        setMessages(prev => prev.map(m =>
          m._tempId === tempId ? { ...m, status: 'sending' } : m
        ));
        httpSendQueue.current.push({ tempId, body: item.body, replyId: item.reply_to_id, attachments: item.attachments || [] });
      });
      // drainHttpSendQueue isn't available here (closure), use ref workaround
      drainHttpSendQueueRef.current?.();
    }, 1000);
    })();
    return () => { cancelled = true; };
  }, [appId, convId]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendViaHttp = useCallback(async (tempId, finalBody, replyId, attachments) => {
    // Prevent duplicate concurrent POSTs for the same message
    if (inFlight.current[tempId]) {
      console.log(`[CHAT][SEND][SKIP] already in-flight tempId=${tempId}`);
      return;
    }
    // Skip if socket already confirmed this message (message_saved cleared failedQueue)
    if (!failedQueue.current[tempId]) {
      console.log(`[CHAT][SEND][SKIP] already confirmed by socket tempId=${tempId}`);
      return;
    }
    inFlight.current[tempId] = true;
    console.log(`[CHAT][SEND][START] tempId=${tempId} convId=${convId}`);
    setMessages(prev => prev.map(m =>
      m._tempId === tempId ? { ...m, status: 'sending' } : m
    ));
    try {
      const token = await getToken();
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15s hard timeout
      let res;
      try {
        res = await fetch(
          `${process.env.REACT_APP_API_URL || '/api'}/apps/${appId}/chat/${convId}/messages`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ body: finalBody, reply_to_id: replyId, attachments, _tempId: tempId }),
            signal: controller.signal,
          }
        );
      } finally {
        clearTimeout(timeoutId);
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { message } = await res.json();
      // Only remove from outbox after confirmed server response
      await removeFromOutbox(appId, convId, tempId);
      delete failedQueue.current[tempId];
      delete retryBackoff.current[tempId];
      console.log(`[CHAT][SEND][SUCCESS] tempId=${tempId} dbId=${message.id}`);
      setMessages(prev => prev.map(m => {
        if (m._tempId !== tempId) return m;
        // If socket already confirmed this (bubble has a real id), just ensure status=sent
        if (m.id && m.id !== null) return { ...m, status: 'sent' };
        return { ...message, status: 'sent', _tempId: tempId };
      }));
    } catch (e) {
      console.warn(`[CHAT][SEND][FAIL] tempId=${tempId} err=${e.message}`);
      // Outbox entry stays — message is NOT lost. Mark failed for retry UI.
      setMessages(prev => prev.map(m =>
        m._tempId === tempId ? { ...m, status: 'failed' } : m
      ));
      // Record this failure and schedule the next eligible auto-retry time
      // (bounded exponential backoff + jitter) — retryFailed() checks this
      // before re-attempting, so repeated triggers (reconnect, 'online',
      // the periodic sweep) don't all fire an immediate retry for a message
      // that just failed. Manual retry (retrySingleMessage, the "Not sent"
      // tap-to-retry UI) intentionally bypasses this — a user-initiated
      // retry should never be blocked by auto-retry pacing.
      const prevAttempts = retryBackoff.current[tempId]?.attempts || 0;
      const attempts = prevAttempts + 1;
      retryBackoff.current[tempId] = { attempts, nextEligibleAt: Date.now() + computeBackoffDelayMs(attempts) };
    } finally {
      delete inFlight.current[tempId];
    }
  }, [appId, convId, getToken]);
  // Keep refs in sync so outbox-restore effect can call them without circular deps
  useEffect(() => { sendViaHttpRef.current = sendViaHttp; }, [sendViaHttp]);

  // Drain the HTTP send queue one at a time — prevents overwhelming the server
  // when many messages are sent rapidly (e.g. rapid keypress spam).
  const drainHttpSendQueue = useCallback(() => {
    if (httpSendRunning.current) return;
    const processNext = async () => {
      const item = httpSendQueue.current.shift();
      if (!item) { httpSendRunning.current = false; return; }
      httpSendRunning.current = true;
      await sendViaHttp(item.tempId, item.body, item.replyId, item.attachments);
      processNext();
    };
    processNext();
  }, [sendViaHttp]);

  const enqueueSendViaHttp = useCallback((tempId, body, replyId, attachments) => {
    // Skip if this tempId is already queued or currently in flight — guards
    // against multiple independent triggers (ack timeout, retryFailed, the
    // outbox-restore retry) all trying to queue the same message at once.
    if (inFlight.current[tempId] || httpSendQueue.current.some(q => q.tempId === tempId)) return;
    httpSendQueue.current.push({ tempId, body, replyId, attachments });
    drainHttpSendQueue();
  }, [drainHttpSendQueue]);

  useEffect(() => { drainHttpSendQueueRef.current = drainHttpSendQueue; }, [drainHttpSendQueue]);

  // Retry all failed messages for this conversation via HTTP (silently — no status flash).
  // Goes through the same serialized queue as normal sends — firing every retry
  // in parallel (the old behavior) meant a big offline backlog reconnecting all
  // at once could blast out dozens of simultaneous POSTs, and any one of them
  // timing out/erroring under that burst left its bubble stuck on "Not sent"
  // even though most of the batch would otherwise succeed.
  //
  // This function itself is called from several independent triggers
  // (reconnect catch-up, the 'online' event, a 10s periodic sweep) — without
  // per-message backoff, a message that keeps failing (e.g. a sustained
  // server-side outage) would be retried on every single one of those
  // triggers with no growing delay, which is the retry-storm pattern this
  // guards against. retryBackoff gates each message independently: skip it
  // here if its next-eligible-retry time hasn't passed yet.
  const retryFailed = useCallback(() => {
    const entries = Object.values(failedQueue.current);
    if (!entries.length) return;
    const now = Date.now();
    entries.forEach(item => {
      const tempId = item._tempId;
      if (inFlight.current[tempId] || httpSendQueue.current.some(q => q.tempId === tempId)) return;
      const backoff = retryBackoff.current[tempId];
      if (backoff && backoff.nextEligibleAt > now) return; // not yet eligible — still backing off
      httpSendQueue.current.push({ tempId, body: item.body, replyId: item.reply_to_id, attachments: item.attachments || [] });
    });
    drainHttpSendQueue();
  }, [drainHttpSendQueue]);

  const drainSendQueue = useCallback(() => {
    if (sendQueueRunning.current) return;
    sendQueueRunning.current = true;

    const processNext = () => {
      const item = sendQueue.current.shift();
      if (!item) { sendQueueRunning.current = false; return; }

      const { body, reply_to_id, attachments, tempId } = item;
      const s = socket || getSocket();

      if (!s?.connected || !socketJoined.current) {
        sendQueue.current.unshift(item);
        sendQueueRunning.current = false;
        return;
      }

      s.emit('send_message', {
        conversation_id: convId,
        body,
        reply_to_id,
        attachments,
        _tempId: tempId,
      }, (ack) => {
        clearTimeout(sendTimeouts.current[tempId]);
        delete sendTimeouts.current[tempId];
        if (ack?.ok) {
          // ack.ok means the server ACCEPTED the send attempt (queued for
          // persistence) — it is NOT confirmation the message was saved.
          // Do not clear failedQueue, remove the outbox entry, or mark
          // 'sent' here; the message_saved handler elsewhere is the only
          // thing that may do that, once Postgres actually has the row.
          // Leaving the message in its current (optimistic 'sending')
          // status is correct until that real confirmation arrives.
        } else if (ack?.error === 'rate_limited') {
          sendQueue.current.unshift(item);
          sendQueueRunning.current = false;
          // Honor the server's retryAfterMs hint (Phase 1) instead of a
          // fixed delay — falls back to 2000ms if the server didn't send one.
          setTimeout(drainSendQueue, ack.retryAfterMs || 2000);
          return;
        } else {
          if (failedQueue.current[tempId]) {
            setMessages(prev => prev.map(m =>
              m._tempId === tempId ? { ...m, status: 'failed' } : m
            ));
          }
        }
        processNext();
      });

      clearTimeout(sendTimeouts.current[tempId]);
      sendTimeouts.current[tempId] = setTimeout(() => {
        // Only show "Not sent" if we're still connected — a disconnect-caused timeout
        // will be auto-retried on reconnect, so no need to alarm the user
        if (failedQueue.current[tempId] && socketJoined.current) {
          setMessages(prev => prev.map(m =>
            m._tempId === tempId ? { ...m, status: 'failed' } : m
          ));
        }
        processNext();
      }, 8000);
    };

    processNext();
  }, [socket, convId]);

  // Read state must survive a disconnect: emit via socket when live for the
  // instant read_receipt broadcast, but also persist via HTTP so a message
  // genuinely read while offline doesn't silently fail to record and later
  // look "unread" to the sender / on reconnect resync.
  // Declared before the socket-connection effect below, which references it
  // in onJoined()'s reconnect resync — a build with strict const hoisting
  // (unlike dev's Fast Refresh) throws a TDZ ReferenceError if this sits
  // after that effect instead of before it.
  // sourceMessages, when given, is used instead of messagesStateRef — a
  // setMessages(prev => { ...; return prev; }) read-trick was used here
  // previously to read "current" messages synchronously, but that only works
  // inside a genuine React event handler; called from a Promise .finally()
  // (as every caller here is — after an async loadMessages()/catch-up fetch),
  // React does not guarantee the updater callback runs before the very next
  // line, so last_read_message_id could still come out null even once
  // messages state was already fully loaded. messagesStateRef has the same
  // problem one level removed: it's only kept current by a passive effect
  // that runs after React commits, which can likewise still be pending when
  // a .finally() callback fires. Passing the just-fetched array explicitly
  // (loadMessages() now returns it) sidesteps both races entirely.
  const markRead = useCallback((sourceMessages = null) => {
    const list = sourceMessages || messagesStateRef.current || [];
    const realIds = list
      .filter(m => !m._tempId && m.id != null)
      .map(m => Number(m.id))
      .filter(Number.isFinite);
    const lastReadMessageId = realIds.length > 0 ? Math.max(...realIds) : null;
    if (process.env.NODE_ENV !== 'production') {
      console.debug('[markRead]', { conversationId: convId, sourceLength: list.length, lastReadMessageId });
    }
    const s = socket || getSocket();
    if (s?.connected) {
      s.emit('mark_read', { conversation_id: convId, last_read_message_id: lastReadMessageId });
    } else {
      api.chatMarkRead(appId, convId, lastReadMessageId).catch(() => {});
    }
    // Every markRead() call means this conversation is now read — resync the
    // sidebar nav badge immediately rather than relying solely on the
    // 'message_saved'/'read_receipt' socket round-trip to trigger its own
    // refresh. Previously only handleSelectConv (clicking a conversation)
    // notified the badge, so a read triggered any other way (e.g. a message
    // arriving while this conversation was already open) never told the
    // sidebar to recheck, leaving it stuck on a stale count.
    notifyChatUnreadChanged();
  }, [socket, convId, appId]);

  // Track socket connection state; flush any queued message once the server
  // confirms the join (tenantPool is ready) — not just on TCP reconnect.
  useEffect(() => {
    const s = socket || getSocket();
    if (!s) return;
    setSocketConnected(s.connected);
    const onConnect = () => {
      setSocketConnected(true);
      socketJoined.current = false; // joined confirmation not yet received
    };
    const onJoined = () => {
      socketJoined.current = true;
      // Catch up on messages missed while disconnected, then retry any that truly failed.
      // We must wait for the catch-up fetch to finish before retrying — otherwise messages
      // that the server already saved (ack never arrived due to disconnect) get re-sent.
      let lastId = null;
      setMessages(prev => {
        // Use the max id, not the array-position-last real message — out-of-order
        // confirmation (BullMQ processes sends concurrently, no ordering guarantee)
        // means the last real message in array order isn't necessarily the highest
        // id. Using array position as the catch-up cursor could point `after` at an
        // id lower than messages already confirmed, causing them to be re-fetched
        // and mis-merged against still-optimistic ghosts elsewhere in the array.
        const realIds = prev.filter(m => !m._tempId && m.id != null).map(m => m.id);
        lastId = realIds.length > 0 ? Math.max(...realIds) : null;
        return prev;
      });
      const catchUp = lastId ? loadMessages(null, lastId) : loadMessages();
      Promise.resolve(catchUp)
        .then(
          (freshMsgs) => { retryFailed(); return freshMsgs; },
          (err) => { retryFailed(); throw err; },
        )
        // Re-send our own read cursor too — a mark_read emitted right as the
        // socket dropped never reached the server, and there's no outbox for
        // it (unlike sends), so without this the read state silently reverts
        // for messages that were genuinely seen while briefly offline. Must
        // wait for the catch-up fetch above to land first — calling markRead()
        // synchronously here raced it, so messages could still be empty/stale
        // and last_read_message_id would be sent as null, leaving the server
        // cursor stuck (it only ever moves forward) even though last_read_at
        // kept advancing on every join. Pass the freshly fetched array
        // straight through rather than reading messages/messagesStateRef,
        // which are not guaranteed to reflect it yet at this point — see the
        // comment on markRead's definition.
        .then((freshMsgs) => markRead(freshMsgs))
        .catch(() => markRead());
      // Also re-sync pinned messages — a message_pinned broadcast missed
      // during the disconnect otherwise leaves the pinned banner stale
      // (or missing entirely) until the whole conversation list reloads.
      api.chatPinnedMessages(appId, convId)
        .then(({ pinned_messages }) => setPinnedMessages(pinned_messages || []))
        .catch(() => {});
    };
    const onDisconnect = () => {
      setSocketConnected(false);
      socketJoined.current = false;
      sendQueueRunning.current = false;
      // Clear all failure timers — outbox items will be retried on rejoin, not timed out
      Object.keys(sendTimeouts.current).forEach(id => clearTimeout(sendTimeouts.current[id]));
      sendTimeouts.current = {};
      // Hide any "Not sent" indicators while offline — will auto-retry on reconnect
      setMessages(prev => prev.map(m =>
        m._tempId && m.status === 'failed' ? { ...m, status: 'sending' } : m
      ));
    };
    // If already joined (page-load with live socket), mark ready immediately
    if (s.connected) socketJoined.current = true;
    s.on('connect', onConnect);
    s.on('joined', onJoined);
    s.on('disconnect', onDisconnect);
    return () => {
      s.off('connect', onConnect);
      s.off('joined', onJoined);
      s.off('disconnect', onDisconnect);
    };
  }, [socket, convId, loadMessages, retryFailed, markRead]);

  // Auto-retry when browser goes back online
  useEffect(() => {
    const handler = () => { retryFailed(); };
    window.addEventListener('online', handler);
    return () => window.removeEventListener('online', handler);
  }, [retryFailed]);

  // Periodic retry every 10s for any stuck outbox items. Skipped while the
  // browser is actually offline — otherwise this timer's HTTP attempt fails
  // and flips the bubble back to 'failed' (showing "Not sent") right after
  // the disconnect handler had hidden it as 'sending', causing a visible
  // flicker every ~10s until the connection genuinely comes back.
  useEffect(() => {
    const id = setInterval(() => {
      if (navigator.onLine && Object.keys(failedQueue.current).length > 0) retryFailed();
    }, 10000);
    return () => clearInterval(id);
  }, [retryFailed]);

  useEffect(() => {
    const s = socket || getSocket();
    if (!s) return;
    const handler = (msg) => {
      if (String(msg.conversation_id) !== String(convId)) return;
      const tempId = msg._tempId;
      console.log(`[NM][RECV] tempId=${tempId} id=${msg.id} status=${msg.status} inFailedQueue=${!!failedQueue.current[tempId]}`);
      if (tempId) {
        // Sender already has this optimistic message in UI — skip duplicate.
        // Also skip if message_saved already confirmed it (no _tempId left but id matches).
        setMessages(prev => {
          const hasByTemp = prev.some(m => m._tempId === tempId);
          const hasByid = msg.id && prev.some(m => m.id === msg.id);
          if (hasByTemp) { console.log(`[NM][SKIP-TEMPID] ${tempId} prevLen=${prev.length}`); return prev; }
          if (hasByid)   { console.log(`[NM][SKIP-ID] ${tempId} id=${msg.id} prevLen=${prev.length}`); return prev; }
          console.log(`[NM][ADD] ${tempId} id=${msg.id} status=${msg.status} prevLen=${prev.length}`);
          return [...prev, { ...msg, status: 'sent' }];
        });
      } else {
        let nextMessages = null;
        setMessages(prev => {
          if (prev.some(m => m.id === msg.id)) { nextMessages = prev; return prev; }
          nextMessages = [...prev, msg];
          return nextMessages;
        });
        // System messages (nudges, removals, etc.) only ever emit 'new_message',
        // never 'message_saved' — without this, the conversation is open but its
        // read cursor never advances past a system message, so it can later show
        // as unread even for the user who triggered it (e.g. the nudge sender).
        // Pass nextMessages explicitly rather than calling markRead() bare —
        // that fallback reads messagesStateRef.current, which is synced by a
        // separate passive effect and is still one render behind this
        // setMessages call, so it sent the read cursor without this very
        // message included, leaving it stuck as unread until the
        // conversation was reopened.
        if (msg.message_type === 'system') markRead(nextMessages);
      }
      setTimeout(() => {
        const el = messagesRef.current;
        if (el) el.scrollTop = el.scrollHeight;
      }, 50);
    };
    s.on('new_message', handler);
    return () => s.off('new_message', handler);
  }, [socket, convId, markRead]);

  // message_saved: buffer events into savedQueue, then drain in useEffect.
  // Using flushSync inside a socket event that fires during another React flush causes
  // the updater to run multiple times on different prev values, corrupting the list.
  // Buffering + draining in useEffect runs in a clean React update cycle instead.
  useEffect(() => {
    const s = socket || getSocket();
    if (!s) return;
    const handler = (msg) => {
      if (String(msg.conversation_id) !== String(convId)) return;
      const tempId = msg._tempId || msg.client_message_id;
      console.log(`[CHAT][SOCKET][MESSAGE_SAVED] tempId=${tempId} dbId=${msg.id}`);
      savedQueue.current.push({ msg, tempId });
      // Drain in next microtask so React processes all queued saves in one batch
      Promise.resolve().then(() => {
        const pending = savedQueue.current.splice(0);
        if (pending.length === 0) return;
        setMessages(prev => {
          let next = prev;
          for (const { msg: m, tempId: tid } of pending) {
            const byTemp = tid ? next.findIndex(x => x._tempId === tid) : -1;
            const byId   = next.findIndex(x => x.id === m.id);
            const confirmed = { ...m, client_message_id: undefined, _tempId: undefined, status: 'sent' };
            if (byId !== -1) {
              // Update confirmed message status; also remove any lingering optimistic bubble for same tempId
              next = next
                .filter(x => !(x._tempId === tid && x.id === null))
                .map(x => x.id === m.id ? { ...x, status: 'sent' } : x);
              if (tid) { removeFromOutbox(appId, convId, tid); delete failedQueue.current[tid]; }
            } else if (byTemp !== -1) {
              removeFromOutbox(appId, convId, tid);
              delete failedQueue.current[tid];
              next = [...next];
              next[byTemp] = confirmed;
            } else {
              removeFromOutbox(appId, convId, tid);
              delete failedQueue.current[tid];
              const withoutGhost = next.filter(x => !(x.id === null && x._tempId === tid));
              if (!withoutGhost.some(x => x.id === m.id)) {
                next = [...withoutGhost, confirmed];
              } else {
                next = withoutGhost;
              }
            }
          }
          return next;
        });
        setTimeout(() => {
          const el = messagesRef.current;
          if (el) el.scrollTop = el.scrollHeight;
        }, 50);
      });
    };
    s.on('message_saved', handler);
    return () => s.off('message_saved', handler);
  }, [socket, convId, appId]);

  // Clear typing state when switching conversations
  useEffect(() => {
    setTypingUsers([]);
    Object.values(typingTimers.current).forEach(clearTimeout);
    typingTimers.current = {};
  }, [convId]);

  // Listen for typing events from other members
  useEffect(() => {
    const s = socket || getSocket();
    if (!s) return;
    const handler = ({ conversation_id, user_id, is_typing }) => {
      if (String(conversation_id) !== String(convId)) return;
      if (String(user_id) === String(myUserId)) return;
      clearTimeout(typingTimers.current[user_id]);
      if (is_typing) {
        setTypingUsers(prev => prev.includes(user_id) ? prev : [...prev, user_id]);
        typingTimers.current[user_id] = setTimeout(() => {
          setTypingUsers(prev => prev.filter(id => id !== user_id));
        }, 3000);
      } else {
        setTypingUsers(prev => prev.filter(id => id !== user_id));
      }
    };
    s.on('typing', handler);
    return () => s.off('typing', handler);
  }, [socket, convId, myUserId]);

  // Rotate through typing users every 2s when multiple people are typing
  useEffect(() => {
    clearInterval(typingRotateTimer.current);
    if (isGroup && typingUsers.length > 1) {
      typingRotateTimer.current = setInterval(() => {
        setTypingRotateIdx(i => (i + 1) % typingUsers.length);
      }, 2000);
    } else {
      setTypingRotateIdx(0);
    }
    return () => clearInterval(typingRotateTimer.current);
  }, [isGroup, typingUsers]);

  useEffect(() => {
    const s = socket || getSocket();
    if (!s) return;
    const handler = ({ message_id, conversation_id }) => {
      if (String(conversation_id) !== String(convId)) return;
      setMessages(prev => prev.map(m =>
        m.id === message_id ? { ...m, deleted_at: new Date().toISOString() } : m
      ));
      setPinnedMessages(prev => {
        const next = prev.filter(p => p.id !== message_id);
        setPinnedIndex(i => Math.min(i, Math.max(0, next.length - 1)));
        return next;
      });
    };
    s.on('message_deleted', handler);
    return () => s.off('message_deleted', handler);
  }, [socket, convId]);

  useEffect(() => {
    const s = socket || getSocket();
    if (!s) return;
    const handler = ({ conversation_id, reader_id, read_at }) => {
      if (String(conversation_id) !== String(convId)) return;
      if (String(reader_id) === String(myUserId)) return;
      setMemberReadAt(prev => {
        const existing = prev[reader_id];
        if (existing && existing >= read_at) return prev;
        return { ...prev, [reader_id]: read_at };
      });
    };
    s.on('read_receipt', handler);
    return () => s.off('read_receipt', handler);
  }, [socket, convId, myUserId]);

  // Reaction socket
  useEffect(() => {
    const s = socket || getSocket();
    if (!s) return;
    const handler = ({ message_id, conversation_id, reactions }) => {
      if (String(conversation_id) !== String(convId)) return;
      setMessages(prev => prev.map(m => m.id === message_id ? { ...m, reactions } : m));
    };
    s.on('message_reaction', handler);
    return () => s.off('message_reaction', handler);
  }, [socket, convId]);

  // Pin/unpin socket
  useEffect(() => {
    const s = socket || getSocket();
    if (!s) return;
    const handler = ({ conversation_id, pinned_messages }) => {
      if (String(conversation_id) !== String(convId)) return;
      setPinnedMessages(pinned_messages || []);
      setPinnedIndex(i => Math.min(i, Math.max(0, (pinned_messages || []).length - 1)));
    };
    s.on('message_pinned', handler);
    return () => s.off('message_pinned', handler);
  }, [socket, convId]);

  // Edit socket
  useEffect(() => {
    const s = socket || getSocket();
    if (!s) return;
    const handler = ({ message_id, conversation_id, body, edited_at, attachments }) => {
      if (String(conversation_id) !== String(convId)) return;
      setMessages(prev => prev.map(m => {
        if (m.id !== message_id) return m;
        const next = { ...m, body, edited_at };
        if (attachments !== undefined) next.attachments = attachments;
        return next;
      }));
      // The pinned banner reads from its own pinnedMessages state, not
      // `messages` — without this, editing a currently-pinned message
      // wouldn't update the banner's text until the page was refreshed.
      setPinnedMessages(prev => prev.map(p =>
        p.id === message_id ? { ...p, body, edited_at } : p
      ));
    };
    s.on('message_edited', handler);
    return () => s.off('message_edited', handler);
  }, [socket, convId]);

  // Nudge socket — shake the bell icon when this conversation is nudged
  useEffect(() => {
    const s = socket || getSocket();
    if (!s) return;
    const handler = ({ conversation_id, sender_id }) => {
      if (String(conversation_id) !== String(convId)) return;
      if (String(sender_id) === String(myUserId)) return;
      setNudgeShake(true);
      setTimeout(() => setNudgeShake(false), 1000);
    };
    s.on('nudge', handler);
    return () => s.off('nudge', handler);
  }, [socket, convId, myUserId]);

  // Removed from group socket
  useEffect(() => {
    const s = socket || getSocket();
    if (!s) return;
    const handler = ({ conversation_id, user_id, removed_by }) => {
      if (String(conversation_id) !== String(convId)) return;
      if (String(user_id) === String(myUserId)) {
        // This user was removed
        setRemovedFromGroup(true);
        setRemovedByName(removed_by || null);
        if (onRemovedFromGroup) onRemovedFromGroup(convId);
      // else: backend already emits the system message via new_message — no local injection needed
      }
    };
    s.on('member_removed', handler);
    return () => s.off('member_removed', handler);
  }, [socket, convId, myUserId, onRemovedFromGroup]);

  // Admin change socket
  useEffect(() => {
    const s = socket || getSocket();
    if (!s) return;
    const handler = ({ conversation_id, user_id, is_admin }) => {
      if (String(conversation_id) !== String(convId)) return;
      onConvUpdated({
        ...conv,
        members: (conv.members || []).map(m =>
          String(m.id) === String(user_id) ? { ...m, is_admin } : m
        ),
        is_group_admin: String(user_id) === String(myUserId) ? is_admin : conv.is_group_admin,
      });
    };
    s.on('member_admin_changed', handler);
    return () => s.off('member_admin_changed', handler);
  }, [socket, convId, conv, myUserId, onConvUpdated]);

  const sendNudge = () => {
    const s = socket || getSocket();
    if (!s?.connected || nudging) return;
    s.emit('nudge', { conversation_id: convId });
    setNudging(true);
    setTimeout(() => setNudging(false), 2000);
  };

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    try { localStorage.setItem(`chat_mute_${conv.id}`, JSON.stringify(next)); } catch {}
  };

  // Wait for the initial history fetch to finish before marking read — firing
  // immediately on conversation-open ran against messages==[] (still loading),
  // so markRead() computed last_read_message_id as null. The backend only
  // ever advances last_read_message_id forward (GREATEST(existing, incoming)),
  // so a null send left it stuck at whatever it was set to previously; new
  // messages kept comparing against that stale id and stayed "unread" forever,
  // even though last_read_at did update — this is what caused the sidebar
  // badge (and the per-conversation count) to survive a read and a refresh.
  //
  // Pass `messages` (this effect's own dependency, guaranteed current at the
  // time it runs) instead of calling markRead() with no argument — that
  // fallback reads messagesStateRef.current, which is synced by a separate
  // passive effect and can still be one render behind when `loading` flips
  // to false inside loadMessages()'s .finally(), sending a stale/lower
  // last_read_message_id (or null) and leaving the conversation showing
  // unread until it's reopened.
  useEffect(() => {
    if (loading) return;
    markRead(messages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markRead, loading]);

  // Mark read whenever a new message arrives while this chat is open.
  // Regular messages only ever emit 'message_saved' now (Phase 1 removed the
  // pre-persistence 'new_message' broadcast for them) — this previously
  // listened for 'new_message', which meant a message arriving while the
  // conversation was already open never re-triggered markRead(), leaving the
  // conversation's read cursor stuck at whatever it was on open.
  useEffect(() => {
    const s = socket || getSocket();
    if (!s) return;
    const handler = (msg) => {
      if (String(msg.conversation_id) !== String(convId)) return;
      // Don't call markRead() bare here — its messagesStateRef.current
      // fallback races the *other* message_saved listener (the one that
      // appends this same message to `messages` state) plus the passive
      // effect that syncs the ref, so it can still fire with this very
      // message missing and send a stale/lower last_read_message_id,
      // leaving the conversation looking unread until reopened. Merge the
      // incoming message into the latest known list explicitly instead.
      const base = messagesStateRef.current || [];
      // A prior optimistic bubble may already carry this same id (assigned by
      // the other message_saved handler) while still keeping its _tempId set —
      // that earlier check (`base.some(m => m.id === msg.id)`) then reused the
      // stale entry as-is instead of the confirmed one, so it stayed excluded
      // from markRead's own "last real id" calc (which filters out anything
      // still carrying _tempId), silently keeping the read cursor one message
      // behind. Always replace/append the confirmed message explicitly.
      const idx = base.findIndex(m => m.id === msg.id || (msg._tempId && m._tempId === msg._tempId));
      const confirmed = { ...msg, _tempId: undefined };
      const merged = idx === -1
        ? [...base, confirmed]
        : base.map((m, i) => (i === idx ? confirmed : m));
      markRead(merged);
    };
    s.on('message_saved', handler);
    return () => s.off('message_saved', handler);
  }, [socket, convId, markRead]);

  const CHAR_LIMIT = 2000;

  const sendWithOptimistic = useCallback((body, reply_to_id, attachments, tempId) => {
    const s = socket || getSocket();
    if (!s?.connected || !socketJoined.current) return false;
    // Push to serial queue instead of firing directly
    sendQueue.current.push({ body, reply_to_id, attachments, tempId });
    drainSendQueue();
    return true;
  }, [socket, convId, drainSendQueue]);

  const retrySingleMessage = useCallback((item) => {
    // Manual (user-tapped) retry intentionally bypasses the auto-retry
    // backoff gate in retryFailed() — a deliberate user action should never
    // be silently blocked by pacing meant for automatic background retries.
    // Clear any existing backoff window so a subsequent automatic trigger
    // doesn't inherit stale timing from before this manual attempt.
    delete retryBackoff.current[item._tempId];
    setMessages(prev => prev.map(m =>
      m._tempId === item._tempId ? { ...m, status: 'sending' } : m
    ));
    sendViaHttp(item._tempId, item.body, item.reply_to_id, item.attachments || []);
  }, [sendViaHttp]);

  const send = async () => {
    if (editingMsg) { handleEdit(); return; }
    const { bodyWithMarkers, files } = extractComposeData();
    if (!bodyWithMarkers && files.length === 0) return;
    if (bodyWithMarkers.length > CHAR_LIMIT) return;

    let attachments = [];
    let finalBody = bodyWithMarkers;
    if (files.length > 0) {
      try {
        attachments = await uploadFiles(files);
      } catch (e) {
        alert(e.message || 'File upload failed');
        return;
      }
    }

    const replyId = replyTo?.id || null;

    // Clear compose immediately for responsive feel
    if (composeRef.current) composeRef.current.innerHTML = '';
    setText('');
    savedRange.current = null;
    setReplyTo(null);
    clearDraft(appId, convId);
    if (onDraftChange) onDraftChange(convId, '');

    // Stop typing indicator
    const s = socket || getSocket();
    if (s?.connected) {
      clearTimeout(typingStopTimer.current);
      s.emit('typing', { conversation_id: convId, is_typing: false });
    }

    // Generate a temp ID for this message
    const tempId = `tmp_${Date.now()}_${Math.random().toString(36).slice(2)}`;

    // Add optimistic message to UI immediately
    const optimistic = {
      _tempId: tempId,
      id: null,
      conversation_id: convId,
      sender_id: myUserId,
      sender: { id: myUserId, name: (conv.members || []).find(m => String(m.id) === String(myUserId))?.name || 'You' },
      body: finalBody,
      attachments,
      reply_to: replyId ? replyTo : null,
      created_at: new Date().toISOString(),
      status: 'sending',
    };
    flushSync(() => setMessages(prev => [...prev, optimistic]));
    setTimeout(() => {
      const el = messagesRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }, 30);

    // Persist to outbox BEFORE any send attempt — awaited so the durable
    // record genuinely exists before the socket/HTTP call goes out, not just
    // fired-and-forgotten alongside it. addToOutbox rejects if IndexedDB is
    // available but the write itself failed (quota, transaction error) —
    // caught here rather than left unhandled, since the send must still
    // proceed: the in-memory failedQueue above already has this message and
    // the existing retry machinery works within the session regardless;
    // only the survive-a-refresh guarantee is lost for this one message,
    // same as when IndexedDB isn't available at all (outboxDurable banner).
    const outboxItem = { body: finalBody, reply_to_id: replyId, attachments, _tempId: tempId, created_at: new Date().toISOString() };
    failedQueue.current[tempId] = outboxItem;
    try {
      await addToOutbox(appId, convId, outboxItem);
    } catch (e) {
      console.error(`[CHAT][OUTBOX] durable write failed for tempId=${tempId}, continuing without durability for this message:`, e.message);
    }

    // Prefer socket send (instant, no token overhead). Fall back to HTTP queue
    // only when the socket is disconnected or rejects the message.
    // Single fallback-to-HTTP timer per send — previously there were two
    // independent timers (an 8s "auto-retry" and a 10s ack-timeout) that could
    // both fire for the same tempId while offline/slow, each enqueuing its own
    // HTTP retry for the same message with no dedup on the queue itself.
    let httpFallbackFired = false;
    const fallbackToHttp = () => {
      if (httpFallbackFired || !failedQueue.current[tempId]) return;
      httpFallbackFired = true;
      enqueueSendViaHttp(tempId, finalBody, replyId, attachments);
    };

    const sock = socket || getSocket();
    if (sock?.connected) {
      let ackReceived = false;
      const ackTimeout = setTimeout(() => {
        if (!ackReceived) fallbackToHttp();
      }, 8000);
      // The socket ack fires the instant the server *receives* send_message —
      // before the BullMQ job even runs (socket.js emits ack before enqueue()).
      // So a successful ack does NOT mean the message was actually persisted:
      // if the DB write / BullMQ job later fails (Redis hiccup, tenant pool
      // error, all 3 attempts exhausted), the server never tells the client
      // and this bubble would stay a ghost (_tempId set, id null) forever —
      // that stuck ghost is what produced the duplicate/flicker bug on
      // reconnect once other messages around it confirmed out of order.
      // Guard against that by still requiring an actual persisted-confirmation
      // (message_saved / new_message carrying a real id) within a longer
      // window even after the ack succeeds; fall back to HTTP if it never
      // shows up. The HTTP path is idempotent on client_message_id server-side
      // so this can never create a true duplicate row.
      let confirmed = false;
      const confirmTimeout = setTimeout(() => {
        if (!confirmed) reconcileThenFallback();
      }, 15000);
      const onConfirmed = (msg) => {
        if (msg?._tempId === tempId || msg?.client_message_id === tempId) {
          confirmed = true;
          clearTimeout(confirmTimeout);
          sock.off('message_saved', onConfirmed);
          sock.off('send_failed', onSendFailed);
        }
      };
      // Server-side signal that the BullMQ job exhausted all retries — fall
      // back immediately instead of waiting out the full confirmTimeout.
      const onSendFailed = ({ _tempId: failedTempId } = {}) => {
        if (failedTempId === tempId) {
          confirmed = true;
          clearTimeout(confirmTimeout);
          sock.off('message_saved', onConfirmed);
          sock.off('send_failed', onSendFailed);
          fallbackToHttp();
        }
      };
      // Reconciliation recovery layer (Phase 1's GET .../messages/status):
      // fires only when confirmTimeout expires with no message_saved and no
      // send_failed — i.e. the ack was received but the real confirmation
      // was lost in transit (e.g. a brief disconnect right after the server
      // saved it). Asks the server whether the row actually exists before
      // assuming a resend is needed, so a genuinely-persisted message isn't
      // needlessly resent via HTTP. Never replaces message_saved/HTTP-201 as
      // the normal confirmation path — this only runs as a last-resort check
      // immediately before what would otherwise be a retry.
      const reconcileThenFallback = async () => {
        if (httpFallbackFired || !failedQueue.current[tempId]) return;
        try {
          const { persisted } = await api.chatMessagesStatus(appId, convId, [tempId]);
          const found = (persisted || []).find(p => p.clientMessageId === tempId);
          if (found && failedQueue.current[tempId]) {
            confirmed = true;
            sock.off('message_saved', onConfirmed);
            sock.off('send_failed', onSendFailed);
            await removeFromOutbox(appId, convId, tempId);
            delete failedQueue.current[tempId];
            // Match the exact canonical shape the message_saved handler
            // produces (see its `confirmed` object above) — clear _tempId
            // and client_message_id so this row can never again be matched
            // as a pending/optimistic entry by any dedup/reconciliation
            // logic keyed on those fields (dedupedMessages, the message_saved
            // handler's own byTemp/byId lookups, a later catch-up fetch).
            setMessages(prev => prev.map(m =>
              m._tempId === tempId
                ? { ...m, id: found.id, created_at: found.createdAt || m.created_at, client_message_id: undefined, _tempId: undefined, status: 'sent' }
                : m
            ));
            return;
          }
        } catch (e) {
          console.warn(`[CHAT][RECONCILE] check failed for tempId=${tempId}:`, e.message);
          // fall through to the normal HTTP fallback — reconciliation is a
          // best-effort recovery layer, not a required step
        }
        fallbackToHttp();
      };
      sock.on('message_saved', onConfirmed);
      sock.on('send_failed', onSendFailed);
      sock.emit('send_message', {
        conversation_id: convId,
        body: finalBody,
        reply_to_id: replyId,
        attachments,
        _tempId: tempId,
      }, (ack) => {
        ackReceived = true;
        clearTimeout(ackTimeout);
        if (ack?.ok === false) {
          // Socket rejected (not_joined, rate-limited, etc.) — fall back to HTTP
          confirmed = true;
          clearTimeout(confirmTimeout);
          sock.off('message_saved', onConfirmed);
          fallbackToHttp();
        }
      });
    } else {
      fallbackToHttp();
    }
  };

  const handleKey = (e) => {
    if (handleMentionKeyDown(e)) return;
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
    if (e.key === 'Escape') {
      if (editingMsg) { setEditingMsg(null); if (composeRef.current) composeRef.current.innerHTML = ''; setText(''); }
      else if (replyTo) setReplyTo(null);
    }
  };

  const handleDelete = async (msgId) => {
    try {
      await api.chatDeleteMessage(appId, msgId);
      // socket 'message_deleted' event handles the UI update for all clients including sender
    } catch (e) {
      alert('Failed to delete message');
    }
  };

  const handleReply = (msg) => {
    setReplyTo(msg);
    composeRef.current?.focus();
  };

  const jumpingToMsgRef = useRef(false);
  const handleReplyClick = async (msgId) => {
    const container = messagesRef.current;
    if (!container) return;
    let el = container.querySelector(`[data-msg-id="${msgId}"]`);
    // Target isn't in the currently-loaded page (e.g. a pinned message from
    // further back in history, before "load previous" was clicked) — keep
    // paging older messages in until it shows up or history is exhausted.
    // Guarded against overlapping calls (e.g. rapid repeat clicks on the
    // pinned banner) since each iteration awaits a real network round trip.
    if (!el && !jumpingToMsgRef.current) {
      jumpingToMsgRef.current = true;
      try {
        let oldestId = (() => {
          const realIds = messagesStateRef.current.filter(m => !m._tempId && m.id != null).map(m => m.id);
          return realIds.length ? Math.min(...realIds) : null;
        })();
        while (!el && oldestId != null) {
          const fetched = await loadMessages(oldestId);
          el = container.querySelector(`[data-msg-id="${msgId}"]`);
          // Fewer than a full page means history is exhausted (same
          // condition loadMessages itself uses to set hasMore) — stop
          // rather than looping on an empty/undersized result forever.
          if (!fetched || fetched.length < 10) break;
          oldestId = Math.min(...fetched.map(m => m.id));
        }
      } finally {
        jumpingToMsgRef.current = false;
      }
    }
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightedMsgId(msgId);
      setTimeout(() => setHighlightedMsgId(null), 1500);
    }
  };

  const handleForward = (msg) => {
    setForwardMode(true);
    setForwardSelected(new Set([msg.id]));
  };

  const cancelForward = () => {
    setForwardMode(false);
    setForwardSelected(new Set());
    setForwardLimitWarning(false);
    setShowForwardModal(false);
  };

  const FORWARD_MESSAGE_LIMIT = 10;

  const toggleForwardSelect = (msgId) => {
    setForwardSelected(prev => {
      if (!prev.has(msgId) && prev.size >= FORWARD_MESSAGE_LIMIT) {
        setForwardLimitWarning(true);
        return prev;
      }
      setForwardLimitWarning(false);
      const next = new Set(prev);
      if (next.has(msgId)) next.delete(msgId); else next.add(msgId);
      return next;
    });
  };

  const handlePin = async (msg) => {
    try {
      await api.chatPinMessage(appId, convId, msg.id);
    } catch (e) {
      alert(e?.response?.data?.error || 'Failed to pin/unpin message');
    }
  };

  const handleReact = async (msgId, emoji) => {
    try {
      await api.chatReact(appId, msgId, emoji);
    } catch (e) {
      console.error('React failed', e);
    }
  };

  const handleEdit = async () => {
    if (!editingMsg) return;
    const { bodyWithMarkers, files } = extractComposeData();
    const body = bodyWithMarkers.replace(/\[\[att:\d+\]\]/g, '').trim();

    // Work out which original attachments were removed
    const originalAttIds = (editingMsg.attachments || []).map(a => a.id);
    const keptAttIds = editingMsgAtts.map(a => a.id);
    const remove_attachment_ids = originalAttIds.filter(id => !keptAttIds.includes(id));

    // Upload any new files added via compose
    let add_attachments = [];
    if (files.length > 0) {
      try {
        add_attachments = await uploadFiles(files);
      } catch (e) {
        alert(e.message || 'File upload failed');
        return;
      }
    }

    if (!body && keptAttIds.length === 0 && add_attachments.length === 0) return;

    try {
      await api.chatEditMessage(appId, editingMsg.id, body, add_attachments, remove_attachment_ids);
      setEditingMsg(null);
      setEditingMsgAtts([]);
      if (composeRef.current) composeRef.current.innerHTML = '';
      setText('');
      clearDraft(appId, convId);
      if (onDraftChange) onDraftChange(convId, '');
    } catch (e) {
      console.error('Failed to edit message', e);
    }
  };

  const handleDeleteGroup = async () => {
    try {
      await api.chatDeleteGroup(appId, convId);
      onConvDeleted(convId);
    } catch (e) {
      alert(e?.response?.data?.error || 'Failed to delete group');
    }
  };

  const loadMore = () => {
    if (messages.length) loadMessages(messages[0].id);
  };

  const isNote = conv.type === 'note';
  const members = conv.members || [];
  const otherUserId = !isGroup ? conv.other_user_id : null;
  const isDmOnline  = !isGroup && otherUserId && onlineUserIds.includes(String(otherUserId));

  const subtitle = isNote
    ? 'Personal notes & reminders'
    : isGroup
      ? `${members.length} members`
      : members.find(m => String(m.id) !== String(myUserId))?.email || '';

  const headerInitial = isNote ? '📝' : (conv.display_name || '?')[0].toUpperCase();
  const dmOtherMember = !isGroup && !isNote ? members.find(m => String(m.id) !== String(myUserId)) : null;
  const headerPhoto = dmOtherMember?.photo_url || null;

  // Pass pinned_messages into conv for MessageBubble
  const pinnedIds = new Set(pinnedMessages.map(p => p.id));
  const convWithPin = { ...conv, pinned_messages: pinnedMessages };

  return (
    <div className="chat-window">
      <div className="chat-window-header">
        <div
          className={`chat-avatar${isGroup ? ' clickable' : ''}`}
          data-initial={headerInitial}
          onClick={isGroup ? () => setShowGroupInfo(v => !v) : undefined}
        >
          {headerPhoto
            ? <img src={headerPhoto} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
            : headerInitial}
        </div>
        <div
          style={{ flex: 1, minWidth: 0, cursor: isGroup ? 'pointer' : 'default' }}
          onClick={isGroup ? () => setShowGroupInfo(v => !v) : undefined}
        >
          <div className="chat-window-title">
            {conv.display_name}
          </div>
          <div className="chat-window-sub">
            {isDmOnline ? (
              <span className="chat-header-online-label">Online</span>
            ) : subtitle}
          </div>
        </div>
        {/* Bell (nudge) + mute — hidden for self-notes */}
        {!isNote && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <button
              className={`chat-header-btn chat-nudge-btn${nudging ? ' nudging' : ''}`}
              title="Ring bell"
              onClick={sendNudge}
              disabled={nudging}
            >
              <Bell size={15} className={nudgeShake ? 'chat-bell-shake' : ''} />
            </button>
            <button
              className={`chat-header-btn${muted ? ' active' : ''}`}
              title={muted ? 'Unmute bells' : 'Mute bells'}
              onClick={toggleMute}
            >
              {muted ? <VolumeX size={15} /> : <BellOff size={15} />}
            </button>
          </div>
        )}
        {/* Group admin actions */}
        {isGroup && isGroupAdmin && (
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="chat-header-btn" title="Edit group" onClick={() => setShowEditGroup(true)}>
              <Edit2 size={15} />
            </button>
            <button className="chat-header-btn danger" title="Delete group" onClick={() => setConfirmDeleteGroup(true)}>
              <Trash2 size={15} />
            </button>
          </div>
        )}
      </div>

      <div className={`chat-window-body${showGroupInfo && isGroup ? ' with-sidebar' : ''}`}>
      <div className="chat-main-col">
      {/* Durable outbox unavailable warning — explicit per approved design:
          never silently represent a pending message as durably queued when
          it isn't. Sending still works this session; only the
          survive-a-refresh guarantee is absent. */}
      {!outboxDurable && (
        <div style={{
          padding: '8px 14px', fontSize: 12.5, background: '#fff3cd', color: '#7a5c00',
          borderBottom: '1px solid #f0d999', display: 'flex', alignItems: 'center', gap: 6,
        }}>
          Offline message storage is unavailable in this browser — messages may be lost if you close this tab before they finish sending.
        </div>
      )}
      {/* Pinned messages banner */}
      {pinnedMessages.length > 0 && (() => {
        const pm = pinnedMessages[pinnedIndex];
        if (!pm || pm.deleted_at) return null;
        return (
          <div className="chat-pinned-banner">
            <Pin size={12} className="chat-pinned-icon" />
            <div className="chat-pinned-content" style={{ cursor: 'pointer' }} onClick={() => handleReplyClick(pm.id)}>
              <span className="chat-pinned-label">
                Pinned message
                {pm.edited_at && <span className="chat-pinned-edited"> · Edited</span>}
                {pinnedMessages.length > 1 && (
                  <span className="chat-pinned-count"> {pinnedIndex + 1}/{pinnedMessages.length}</span>
                )}
              </span>
              <span className="chat-pinned-body">
                {(() => {
                  const cleaned = (pm.body || '').replace(/\[\[(?:att|id):\d+\]\]/g, '').replace(/@\[(\d+):([^\]]+)\]/g, '@$2').trim().slice(0, 100);
                  if (cleaned) return cleaned;
                  const count = Number(pm.attachment_count) || 0;
                  if (count > 1) return `📎 ${count} Attachments`;
                  return `📎 ${pm.first_attachment_name || 'Attachment'}`;
                })()}
              </span>
            </div>
            {pinnedMessages.length > 1 && (
              <div className="chat-pinned-nav">
                <button
                  className="chat-pinned-nav-btn"
                  onClick={() => setPinnedIndex(i => (i - 1 + pinnedMessages.length) % pinnedMessages.length)}
                  title="Previous pinned"
                >‹</button>
                <button
                  className="chat-pinned-nav-btn"
                  onClick={() => setPinnedIndex(i => (i + 1) % pinnedMessages.length)}
                  title="Next pinned"
                >›</button>
              </div>
            )}
            <button className="chat-pinned-unpin" onClick={() => handlePin(pm)} title="Unpin">
              <X size={13} />
            </button>
          </div>
        );
      })()}

      <div className="chat-messages" ref={messagesRef}>
        {loading && <div className="chat-loading">Loading…</div>}
        {!loading && hasMore && (
          <button className="chat-load-more" onClick={loadMore}>↑ Load previous messages</button>
        )}
        {dedupedMessages.filter(m => m.status !== 'sending').map((msg, idx, arr) => {
          const prev = arr[idx - 1];
          const sameAsPrev = prev && String(prev.sender_id) === String(msg.sender_id);
          const msgDate = new Date(msg.created_at).toDateString();
          const prevDate = prev ? new Date(prev.created_at).toDateString() : null;
          const showDateSep = msgDate !== prevDate;
          return (
            <React.Fragment key={msg.id}>
              {showDateSep && (
                <div className="chat-date-separator">
                  <span>{formatDateLabel(msg.created_at)}</span>
                </div>
              )}
              {msg.message_type === 'system' ? (
                <div className="chat-system-message"><span>
                  {String(msg.sender_id) === String(myUserId) && /is calling your attention/.test(msg.body || '')
                    ? '🔔 You nudged the conversation'
                    : msg.body}
                </span></div>
              ) : <MessageBubble
                msg={msg}
                isMine={String(msg.sender_id) === String(myUserId)}
                memberReadAt={memberReadAt}
                conv={convWithPin}
                myUserId={myUserId}
                onDelete={handleDelete}
                onReply={handleReply}
                onPin={handlePin}
                onReact={handleReact}
                onEdit={(msg) => {
                  setEditingMsg(msg);
                  setEditingMsgAtts(msg.attachments || []);
                  const raw = (msg.body || '').replace(/\n?\[\[att:\d+\]\]/g, '').trim();
                  if (composeRef.current) {
                    // Convert @[id:Name] tokens to mention chip spans so they render correctly.
                    // Newlines are converted to <br> last, after escaping/mention conversion —
                    // innerHTML parses literal \n in text as ordinary whitespace and collapses
                    // it (independent of the compose box's white-space:pre-wrap CSS, which only
                    // affects rendering of whatever survives HTML parsing), so without this the
                    // line breaks from the original message are silently lost on edit.
                    const html = raw.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
                      .replace(/@\[(\d+):([^\]]+)\]/g, (_, id, name) =>
                        `<span class="mention-chip" contenteditable="false" data-mention-id="${id}" data-mention-name="${name}">@${name}</span>`
                      )
                      .replace(/\n/g, '<br>');
                    composeRef.current.innerHTML = html;
                  }
                  setText(raw);
                  composeRef.current?.focus();
                }}
                onForward={handleForward}
                isForwardMode={forwardMode}
                isForwardSelected={forwardSelected.has(msg.id)}
                onToggleForwardSelect={() => toggleForwardSelect(msg.id)}
                isGroupAdmin={isGroupAdmin}
                showSender={isGroup ? !sameAsPrev : false}
                sameSender={sameAsPrev}
                onRetry={msg._tempId ? () => retrySingleMessage(failedQueue.current[msg._tempId] || msg) : undefined}
                onReplyClick={handleReplyClick}
                isHighlighted={!!highlightedMsgId && highlightedMsgId === msg.id}
              />}
            </React.Fragment>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <div className="chat-input-area">
        {/* Removed from group banner */}
        {removedFromGroup && (
          <div className="chat-removed-banner">
            {removedByName ? `${removedByName} removed you from this group` : 'You are no longer a participant in this group'}
          </div>
        )}
        {/* Forward mode bar */}
        {forwardMode && (
          <div className="chat-reply-bar" style={{ background: 'var(--accent-soft, rgba(99,102,241,0.08))' }}>
            <Forward size={13} style={{ color: 'var(--accent)' }} />
            <div className="chat-reply-bar-content">
              <span className="chat-reply-bar-sender" style={{ color: forwardLimitWarning ? 'var(--danger, #e5484d)' : 'var(--accent)' }}>
                {forwardSelected.size} message{forwardSelected.size !== 1 ? 's' : ''} selected
              </span>
              <span className="chat-reply-bar-body" style={forwardLimitWarning ? { color: 'var(--danger, #e5484d)' } : undefined}>
                {forwardLimitWarning ? `You can only select up to ${FORWARD_MESSAGE_LIMIT} messages at a time.` : 'Tap messages to select / deselect'}
              </span>
            </div>
            <button
              className="btn-primary"
              disabled={!forwardSelected.size}
              onClick={() => setShowForwardModal(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, padding: '4px 10px' }}
            >
              <Forward size={13} /> Forward
            </button>
            <button className="chat-reply-bar-close" onClick={cancelForward}><X size={13} /></button>
          </div>
        )}
        {/* Edit context bar */}
        {editingMsg && (
          <div className="chat-reply-bar" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Edit2 size={13} />
              <div className="chat-reply-bar-content">
                <span className="chat-reply-bar-sender">Editing message</span>
                <span className="chat-reply-bar-body">{replyBodyPreview(editingMsg)}</span>
              </div>
              <button className="chat-reply-bar-close" onClick={() => { setEditingMsg(null); setEditingMsgAtts([]); if (composeRef.current) composeRef.current.innerHTML = ''; setText(''); }}>
                <X size={13} />
              </button>
            </div>
            {editingMsgAtts.length > 0 && (
              <div style={{ paddingLeft: 20 }}>
                <AttachmentDisplay
                  attachments={editingMsgAtts}
                  onDelete={(a) => setEditingMsgAtts(prev => prev.filter(x => x.id !== a.id))}
                />
              </div>
            )}
          </div>
        )}
        {/* Reply context bar */}
        {replyTo && (
          <div className="chat-reply-bar">
            <Reply size={13} />
            <ReplyAttachmentPreview atts={replyAttachments(replyTo)} />
            <div className="chat-reply-bar-content">
              <span className="chat-reply-bar-sender">{replyTo.sender?.name || 'Unknown'}</span>
              {replyBodyPreview(replyTo) && <span className="chat-reply-bar-body">{replyBodyPreview(replyTo)}</span>}
            </div>
            <button className="chat-reply-bar-close" onClick={() => setReplyTo(null)}>
              <X size={13} />
            </button>
          </div>
        )}
        {typingUsers.length > 0 && (() => {
          const names = typingUsers.map(uid => {
            const m = (conv.members || []).find(m => String(m.id) === String(uid));
            return m ? (m.name || m.first_name || m.email || 'Someone') : 'Someone';
          });
          const label = !isGroup
            ? 'Typing…'
            : `${names[typingRotateIdx % names.length]} is typing…`;
          return (
            <div className="chat-typing-indicator">
              <span className="chat-typing-dots"><span /><span /><span /></span>
              <span className="chat-typing-text">{label}</span>
            </div>
          );
        })()}
        {!socketConnected && (
          <div className="chat-reconnecting-banner">
            <span className="chat-reconnecting-dot" />
            Reconnecting…
          </div>
        )}
        <div className={`chat-input-row${removedFromGroup ? ' chat-input-row--disabled' : ''}`}>
          {showEmojiPicker && !removedFromGroup && (
            <EmojiPicker
              onSelect={(emoji) => {
                restoreRange();
                const sel = window.getSelection();
                if (sel && sel.rangeCount > 0) {
                  const range = sel.getRangeAt(0);
                  const node = document.createTextNode(emoji);
                  range.insertNode(node);
                  range.setStartAfter(node);
                  range.collapse(true);
                  sel.removeAllRanges();
                  sel.addRange(range);
                } else if (composeRef.current) {
                  composeRef.current.innerText += emoji;
                }
                syncTextFromCompose();
                setShowEmojiPicker(false);
              }}
              onClose={() => setShowEmojiPicker(false)}
            />
          )}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, position: 'relative' }}>
            <MentionDropdown />
            {fileErrors.length > 0 && (
              <div className="att-pending-errors">
                {fileErrors.map((e, i) => <div key={i} className="att-pending-error">{e}</div>)}
              </div>
            )}
            <div
              ref={composeRef}
              className="chat-input chat-compose-editable"
              contentEditable={!removedFromGroup}
              suppressContentEditableWarning
              data-placeholder={removedFromGroup ? "You can no longer send messages here" : "Type a message…"}
              onInput={syncTextFromCompose}
              onKeyDown={handleKey}
              onBlur={(e) => { saveRange(e); closeMention(); }}
              onMouseUp={saveRange}
              onKeyUp={saveRange}
              onPaste={(e) => {
                e.preventDefault();
                const items = Array.from(e.clipboardData?.items || []);
                const pastedFiles = items
                  .filter(item => item.kind === 'file')
                  .map(item => item.getAsFile())
                  .filter(Boolean);
                if (pastedFiles.length) {
                  saveRange();
                  addFiles(pastedFiles);
                  return;
                }
                const text = e.clipboardData.getData('text/plain');
                if (text) document.execCommand('insertText', false, text);
              }}
            />
          </div>
          <div className="chat-input-actions">
            <button
              className="chat-emoji-input-btn"
              onClick={() => setShowEmojiPicker(v => !v)}
              title="Emoji"
              disabled={removedFromGroup}
            >
              <Smile size={18} />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPT_STRING}
              style={{ display: 'none' }}
            />
            <button
              className="chat-emoji-input-btn"
              onClick={() => { saveRange(); fileInputRef.current?.click(); }}
              title="Attach file"
              disabled={uploading || removedFromGroup}
            >
              <Paperclip size={18} />
            </button>
            <button className="chat-send-btn" onClick={send} disabled={uploading || removedFromGroup || (!editingMsg && !text.trim() && !composeRef.current?.querySelector('.chat-inline-img-wrap'))}>
              {uploading ? <span style={{ fontSize: 11 }}>…</span> : <Send size={16} />}
            </button>
          </div>
        </div>
        <div className="chat-input-hint">
          <span>Enter to send</span>
          <span className="chat-input-hint-sep">·</span>
          <span>Shift+Enter for new line</span>
          {text.length > 0 && (
            <span className={`chat-char-count-inline${text.length >= CHAR_LIMIT ? ' at-limit' : text.length >= CHAR_LIMIT * 0.9 ? ' near-limit' : ''}`}>
              {text.length}/{CHAR_LIMIT}
            </span>
          )}
        </div>
      </div>
      </div>{/* end chat-main-col */}

      {/* Group info sidebar */}
      {showGroupInfo && isGroup && (
        <GroupInfoSidebar
          appId={appId}
          api={api}
          myUserId={myUserId}
          conv={conv}
          isGroupAdmin={isGroupAdmin}
          onClose={() => setShowGroupInfo(false)}
          onConvUpdated={(updated) => { onConvUpdated(updated); }}
        />
      )}

      </div>{/* end chat-window-body */}

      {showEditGroup && (
        <EditGroupModal
          appId={appId}
          api={api}
          myUserId={myUserId}
          conv={conv}
          onUpdated={(updated) => { setShowEditGroup(false); onConvUpdated(updated); }}
          onClose={() => setShowEditGroup(false)}
        />
      )}

      {confirmDeleteGroup && (
        <DeleteConfirmModal
          message={`Delete group "${conv.display_name}"? This cannot be undone.`}
          onConfirm={() => { setConfirmDeleteGroup(false); handleDeleteGroup(); }}
          onCancel={() => setConfirmDeleteGroup(false)}
        />
      )}

      {showForwardModal && (
        <ForwardModal
          messages={messages.filter(m => forwardSelected.has(m.id))}
          conversations={conversations.filter(c => String(c.id) !== String(conv.id))}
          noteConv={noteConv}
          socket={socket || getSocket()}
          appId={appId}
          myUserId={myUserId}
          api={api}
          onClose={() => setShowForwardModal(false)}
          onSent={() => { setShowForwardModal(false); cancelForward(); }}
        />
      )}
    </div>
  );
}

// ─── Main Chat Page ──────────────────────────────────────────────────────────
export default function Chat() {
  const { appId } = useParams();
  const { getToken } = useAuth();
  const api = useApi();
  const { me } = useWorkspace();
  const myUserId = me?.id;

  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [search, setSearch] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [socket, setSocket] = useState(null);
  const [onlineUserIds, setOnlineUserIds] = useState([]);
  const [convTypingUsers, setConvTypingUsers] = useState({}); // convId → [userId, ...]
  const convTypingTimers = useRef({});
  // drafts: convId (string) → draft text
  const [drafts, setDrafts] = useState(() => {
    // Load all drafts for this appId from localStorage on first render
    const result = {};
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(`chat_draft_${appId}_`)) {
          const cid = k.replace(`chat_draft_${appId}_`, '');
          const val = localStorage.getItem(k);
          if (val && val.trim()) result[cid] = val.trim();
        }
      }
    } catch {}
    return result;
  });

  const handleDraftChange = useCallback((convId, text) => {
    setDrafts(prev => {
      const next = { ...prev };
      if (text && text.trim()) next[String(convId)] = text.trim();
      else delete next[String(convId)];
      return next;
    });
  }, []);

  // One-time migration of any pending sends left in the old localStorage
  // outbox (chat_outbox_{appId}_{convId}) into the new IndexedDB outbox.
  // Runs once at the Chat page level (not per-conversation) since a user's
  // pending message could be in ANY conversation, not just whichever one
  // they open first. Idempotent — safe on every mount; see
  // migrateLegacyLocalStorageOutbox's own completion-flag guard.
  useEffect(() => {
    migrateLegacyLocalStorageOutbox().then((result) => {
      if (result && result.migrated > 0) {
        console.log(`[CHAT][OUTBOX][MIGRATE] moved ${result.migrated} pending message(s) from localStorage to IndexedDB`);
      }
    }).catch((e) => {
      console.error('[CHAT][OUTBOX][MIGRATE] failed:', e.message);
    });
  }, []);

  useEffect(() => {
    let mounted = true;
    getToken().then(token => {
      if (!mounted) return;
      const s = getSocket(token, getToken);

      const doJoin = () => {
        s._joinedAppId = null; // force re-join every connect/reconnect
        s.emit('join', { appId });
        if (mounted) setSocket(s);
      };

      const onConnectError = (err) => console.error('[socket]', err.message);

      // Both initial connect and every reconnect go through the same 'connect' handler
      s.on('connect', doJoin);
      s.on('connect_error', onConnectError);

      if (s.connected) doJoin();

      return () => {
        s.off('connect', doJoin);
        s.off('connect_error', onConnectError);
      };
    });
    return () => { mounted = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, getToken]);

  useEffect(() => {
    api.chatConversations(appId).then(setConversations).catch(() => {});
  }, [appId, api]);

  // Socket: new message → bump conversation
  useEffect(() => {
    const s = socket || getSocket();
    if (!s) return;
    const handler = (msg) => {
      setConversations(prev => {
        const updated = prev.map(c => {
          if (String(c.id) !== String(msg.conversation_id)) return c;
          const isActive = String(c.id) === String(activeConvId);
          return {
            ...c,
            last_message: msg,
            updated_at: msg.created_at,
            unread_count: isActive ? 0 : (c.unread_count || 0) + (String(msg.sender_id) !== String(myUserId) ? 1 : 0),
          };
        });
        return [...updated].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
      });
    };
    // Regular messages only ever emit 'message_saved' now (Phase 1 removed
    // the pre-persistence 'new_message' broadcast for them) — this listener
    // was never updated, so it silently never fired: the list's unread_count,
    // last_message, and sort order never live-updated for any conversation,
    // only briefly correcting themselves on a full page reload.
    s.on('message_saved', handler);
    // System messages (nudges, member add/remove, etc.) only ever emit
    // 'new_message', never 'message_saved' — without this, the list's
    // last_message preview/unread stayed stale after a nudge until the
    // conversation list was re-fetched (switch chats or refresh).
    const systemHandler = (msg) => { if (msg.message_type === 'system') handler(msg); };
    s.on('new_message', systemHandler);
    return () => { s.off('message_saved', handler); s.off('new_message', systemHandler); };
  }, [socket, activeConvId, myUserId]);

  // Socket: new conversation created (recipient gets notified + room joined server-side)
  useEffect(() => {
    const s = socket || getSocket();
    if (!s) return;
    const handler = (conv) => {
      setConversations(prev => {
        if (prev.find(c => String(c.id) === String(conv.id))) return prev;
        return [conv, ...prev];
      });
    };
    s.on('conversation_created', handler);
    return () => s.off('conversation_created', handler);
  }, [socket]);

  // Socket: message deleted — if it was the conversation's last_message
  // (used for the sidebar preview), mark it deleted locally too, so the
  // list shows "This message was deleted" instead of continuing to show
  // stale pre-delete preview text/attachment. The open thread's own message
  // list is handled separately by the per-conversation message_deleted
  // listener in ChatWindow.
  useEffect(() => {
    const s = socket || getSocket();
    if (!s) return;
    const handler = ({ message_id, conversation_id }) => {
      setConversations(prev => prev.map(c => {
        if (String(c.id) !== String(conversation_id)) return c;
        if (!c.last_message || c.last_message.id !== message_id) return c;
        return { ...c, last_message: { ...c.last_message, deleted_at: new Date().toISOString() } };
      }));
    };
    s.on('message_deleted', handler);
    return () => s.off('message_deleted', handler);
  }, [socket]);

  // Socket: presence updates
  useEffect(() => {
    const s = socket || getSocket();
    if (!s) return;
    const handler = ({ online }) => setOnlineUserIds(online || []);
    s.on('presence_update', handler);
    s.emit('get_presence');
    return () => s.off('presence_update', handler);
  }, [socket]);

  // Socket: typing indicator for conversation list
  useEffect(() => {
    const s = socket || getSocket();
    if (!s) return;
    const handler = ({ conversation_id, user_id, is_typing }) => {
      const cid = String(conversation_id);
      const uid = String(user_id);
      const timerKey = `${cid}:${uid}`;
      clearTimeout(convTypingTimers.current[timerKey]);
      if (is_typing) {
        setConvTypingUsers(prev => {
          const existing = prev[cid] || [];
          if (existing.includes(uid)) return prev;
          return { ...prev, [cid]: [...existing, uid] };
        });
        convTypingTimers.current[timerKey] = setTimeout(() => {
          setConvTypingUsers(prev => {
            const existing = (prev[cid] || []).filter(u => u !== uid);
            const next = { ...prev };
            if (existing.length) next[cid] = existing; else delete next[cid];
            return next;
          });
        }, 4000);
      } else {
        setConvTypingUsers(prev => {
          const existing = (prev[cid] || []).filter(u => u !== uid);
          const next = { ...prev };
          if (existing.length) next[cid] = existing; else delete next[cid];
          return next;
        });
      }
    };
    s.on('typing', handler);
    return () => s.off('typing', handler);
  }, [socket]);

  // Socket: group updated by admin
  useEffect(() => {
    const s = socket || getSocket();
    if (!s) return;
    const handler = (updatedConv) => {
      setConversations(prev => prev.map(c => String(c.id) === String(updatedConv.id) ? updatedConv : c));
    };
    s.on('conversation_updated', handler);
    return () => s.off('conversation_updated', handler);
  }, [socket]);

  // Socket: group deleted by admin
  useEffect(() => {
    const s = socket || getSocket();
    if (!s) return;
    const handler = ({ conversation_id }) => {
      setConversations(prev => prev.filter(c => String(c.id) !== String(conversation_id)));
      setActiveConvId(prev => String(prev) === String(conversation_id) ? null : prev);
    };
    s.on('conversation_deleted', handler);
    return () => s.off('conversation_deleted', handler);
  }, [socket]);

  // Socket: someone read a conversation — keep the list-level conversations
  // state's members[].last_read_at fresh. The open conversation's own
  // read-tick UI updates itself live via its own read_receipt listener, but
  // that only touches its local memberReadAt state; whenever a conversation
  // is (re)opened later, memberReadAt reseeds from THIS outer conversations
  // array (see loadMessages' initial seed). Without this handler, a read
  // that arrives while the conversation isn't open never reaches the outer
  // array, so reopening it shows stale (unread-looking) ticks even though
  // the unread badge count — queried fresh from the server — is correct.
  useEffect(() => {
    const s = socket || getSocket();
    if (!s) return;
    const handler = ({ conversation_id, reader_id, read_at }) => {
      setConversations(prev => prev.map(c => {
        if (String(c.id) !== String(conversation_id)) return c;
        const members = (c.members || []).map(m => {
          if (String(m.id) !== String(reader_id)) return m;
          if (m.last_read_at && m.last_read_at >= read_at) return m;
          return { ...m, last_read_at: read_at };
        });
        return { ...c, members };
      }));
    };
    s.on('read_receipt', handler);
    return () => s.off('read_receipt', handler);
  }, [socket]);

  // Socket: current user removed from a group by admin
  useEffect(() => {
    const s = socket || getSocket();
    if (!s) return;
    const handler = ({ conversation_id }) => {
      setConversations(prev => prev.map(c =>
        String(c.id) === String(conversation_id) ? { ...c, _removed: true } : c
      ));
    };
    s.on('member_removed', handler);
    return () => s.off('member_removed', handler);
  }, [socket]);

  const handleSelectConv = (id) => {
    setActiveConvId(id);
    setConversations(prev => prev.map(c => String(c.id) === String(id) ? { ...c, unread_count: 0 } : c));
    notifyChatUnreadChanged();
  };

  const handleCreated = (conv) => {
    setShowNew(false);
    setConversations(prev => {
      const exists = prev.find(c => c.id === conv.id);
      if (exists) return prev;
      return [conv, ...prev];
    });
    setActiveConvId(conv.id);
  };

  const handleConvUpdated = (updated) => {
    setConversations(prev => prev.map(c => String(c.id) === String(updated.id) ? updated : c));
  };

  const handleConvDeleted = (convId) => {
    setConversations(prev => prev.filter(c => String(c.id) !== String(convId)));
    setActiveConvId(null);
  };

  const handleRemovedFromGroup = (convId) => {
    setConversations(prev => prev.map(c =>
      String(c.id) === String(convId) ? { ...c, _removed: true } : c
    ));
  };

  const noteConv = conversations.find(c => c.type === 'note');

  const handleOpenNotes = async () => {
    if (noteConv) { handleSelectConv(noteConv.id); return; }
    try {
      const conv = await api.chatCreateConv(appId, { type: 'note' });
      setConversations(prev => prev.find(c => c.id === conv.id) ? prev : [conv, ...prev]);
      setActiveConvId(conv.id);
    } catch (e) { console.error(e); }
  };

  const filteredConvs = conversations
    .filter(c => c.type !== 'note' && (!search || c.display_name?.toLowerCase().includes(search.toLowerCase())))
    .sort((a, b) => {
      const aDraft = drafts[String(a.id)] ? 1 : 0;
      const bDraft = drafts[String(b.id)] ? 1 : 0;
      if (bDraft !== aDraft) return bDraft - aDraft; // drafts first
      return new Date(b.updated_at || 0) - new Date(a.updated_at || 0);
    });

  const activeConv = conversations.find(c => String(c.id) === String(activeConvId));

  return (
    <div className="chat-layout">
      {/* Sidebar */}
      <div className="chat-sidebar">
        <div className="chat-sidebar-header">
          <span className="chat-sidebar-title">Messages</span>
          <button className="chat-new-btn" onClick={() => setShowNew(true)} title="New conversation">
            <Plus size={16} />
          </button>
        </div>

        <div className="chat-sidebar-search">
          <Search size={14} />
          <input
            placeholder="Search conversations…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        <div
          className={`chat-notes-item${noteConv && String(noteConv.id) === String(activeConvId) ? ' active' : ''}`}
          onClick={handleOpenNotes}
          title="My Notes"
        >
          <span className="chat-notes-icon">📝</span>
          <span className="chat-notes-label">My Notes</span>
          {noteConv && drafts[String(noteConv.id)] && String(noteConv.id) !== String(activeConvId) && (
            <span className="chat-notes-draft-dot" title="Draft saved" />
          )}
        </div>

        <div className="chat-conv-list">
          {filteredConvs.map(c => (
            <ConvItem
              key={c.id}
              conv={c}
              active={String(c.id) === String(activeConvId)}
              onClick={() => handleSelectConv(c.id)}
              isOnline={!c.is_group && c.other_user_id && onlineUserIds.includes(String(c.other_user_id))}
              myUserId={myUserId}
              typingUserIds={convTypingUsers[c.id] || []}
              draft={drafts[String(c.id)] || ''}
            />
          ))}
          {!filteredConvs.length && (
            <div className="chat-empty-small">
              {search ? 'No conversations match' : 'No other conversations yet'}
            </div>
          )}
        </div>
      </div>

      {/* Main panel */}
      <div className="chat-main">
        {activeConv ? (
          <ChatWindow
            key={activeConv.id}
            conv={activeConv}
            appId={appId}
            api={api}
            myUserId={myUserId}
            socket={socket}
            onConvUpdated={handleConvUpdated}
            onConvDeleted={handleConvDeleted}
            onlineUserIds={onlineUserIds}
            conversations={conversations}
            noteConv={noteConv}
            onRemovedFromGroup={handleRemovedFromGroup}
            onDraftChange={handleDraftChange}
          />
        ) : (
          <div className="chat-empty-state">
            <MessageSquare size={48} strokeWidth={1} />
            <p>Select a conversation or start a new one</p>
            <button className="btn-primary" onClick={() => setShowNew(true)}>
              <Plus size={14} /> New Conversation
            </button>
          </div>
        )}
      </div>

      {showNew && (
        <NewConvModal
          appId={appId}
          api={api}
          myUserId={myUserId}
          onCreated={handleCreated}
          onClose={() => setShowNew(false)}
        />
      )}
    </div>
  );
}
